/* organization/OrgIAM — Enterprise Identity & Access Management Console.
   3 tabs: Assignments | Roles | Security
   All permission checks via usePermissions() from the frozen Phase 4B auth layer.
   RBAC engine, Runtime, and Workspace architecture are FROZEN — do not modify. */
import './OrgIAM.css';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { NexusIcon } from '../shared/ui/NexusIcon';
import { Btn, Pill } from '../shared/ui';
import { useToast } from '../shared/feedback';
import { PageHead, StatCard, TabBar } from '../platform/_ui';
import { useOrganization, invalidateRuntime } from './OrganizationRuntimeContext.jsx';
import { usePermissions } from './auth/usePermissions.js';
import { DeniedState } from './auth/DeniedState.jsx';
import { orgRolesApi } from '../api/org-roles.api.js';
import { orgPoliciesApi } from '../api/org-policies.api.js';
import { orgSsoApi } from '../api/org-sso.api.js';
import * as membersApi from '../api/members.api.js';
import * as wsApi from '../api/workspace.api.js';
import * as orgTemplatesApi from '../api/org-templates.api.js';
import { listByOrg as listOrgAudit } from '../api/audit.api.js';
import { listWorkspaces } from '../api/organization.api.js';
import { ORG, WS, CRM, DEV, AI, ANALYTICS } from './auth/capabilities.js';
import { CAP_LABELS, CAP_GROUP_LABELS, groupCapabilities } from './auth/capabilities-meta.js';

/* ── Tabs ─────────────────────────────────────────────────────────────── */
const TABS = [
  { id: 'assignments', label: 'Assignments', icon: 'Inbox'       },
  { id: 'roles',       label: 'Roles',       icon: 'Shield'      },
  { id: 'authentication', label: 'Authentication', icon: 'ShieldCheck' },
];

// Deep-link backward compat — old 12-tab and 6-tab IDs redirect to their new home
const LEGACY_TAB_MAP = {
  'org-roles': 'roles', 'ws-roles': 'roles', 'catalog': 'roles', 'templates': 'roles',
  'member-assignments': 'assignments', 'workspace-access': 'assignments', 'matrix': 'assignments',
  'overview': 'assignments', 'access': 'assignments', 'effective': 'assignments',
  'policies': 'authentication', 'security': 'authentication',
};

/* ── Shared helpers ───────────────────────────────────────────────────── */
const ROLE_TONE  = { ORG_ADMIN: 'purple', MEMBER: 'slate', VIEWER: 'gray' };
const ROLE_LABEL = { ORG_ADMIN: 'Org Admin', MEMBER: 'Member', VIEWER: 'Viewer' };
const WS_ROLE_TONE  = { WORKSPACE_ADMIN: 'purple', WORKSPACE_USER: 'slate' };
const WS_ROLE_LABEL = { WORKSPACE_ADMIN: 'WS Admin', WORKSPACE_USER: 'WS User' };
const SEV_TONE   = { INFO: 'blue', WARNING: 'amber', ERROR: 'red', CRITICAL: 'red' };
const STATUS_TONE = { ACTIVE: 'green', PENDING: 'amber', SUSPENDED: 'red', ARCHIVED: 'gray' };

// L-03: use browser locale instead of hardcoded 'en-IN'
const fmtTs = (v) => {
  if (!v) return '—';
  try { return new Date(v).toLocaleString(undefined, { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }); }
  catch { return String(v); }
};

/* ── Permission Catalog data ─────────────────────────────────────────── */
/* scope: 'org' — shown for Organization roles
   scope: 'ws'  — shown for Workspace roles
   Templates show all groups (scope-agnostic blueprints).
   Split derived from backend WS_ADMIN_CAPABILITIES (org-roles.service.ts) and
   org-permission-resolver.service.ts which confirm the org/ws capability boundary. */
const CATALOG_GROUPS = [
  { scope: 'org', group: 'Organization — Members',    caps: Object.values(ORG).filter(c => c.startsWith('org.members')) },
  { scope: 'org', group: 'Organization — Workspaces', caps: Object.values(ORG).filter(c => c.startsWith('org.workspaces')) },
  { scope: 'org', group: 'Organization — Roles',      caps: Object.values(ORG).filter(c => c.startsWith('org.roles')) },
  { scope: 'org', group: 'Organization — Settings',   caps: Object.values(ORG).filter(c => c.startsWith('org.settings') || c.startsWith('org.branding') || c.startsWith('org.whitelabel')) },
  { scope: 'org', group: 'Organization — Governance', caps: Object.values(ORG).filter(c => c.startsWith('org.audit') || c.startsWith('org.security') || c.startsWith('org.backup') || c.startsWith('org.health')) },
  { scope: 'org', group: 'Organization — Analytics',  caps: [...Object.values(ORG).filter(c => c.startsWith('org.analytics')), ANALYTICS.ORG_USERS, ANALYTICS.ORG_EXPORT, ANALYTICS.WORKSPACE_VIEW] },
  { scope: 'org', group: 'Organization — Operations', caps: Object.values(ORG).filter(c => c.startsWith('org.usage') || c.startsWith('org.search') || c.startsWith('org.templates') || c.startsWith('org.licenses') || c.startsWith('org.support')) },
  { scope: 'org', group: 'Developer',                 caps: Object.values(DEV) },
  { scope: 'org', group: 'AI Platform',               caps: [AI.AGENTS_CONFIGURE, AI.KB_CREATE, AI.MODELS_CONFIGURE] },
  { scope: 'ws',  group: 'Workspace',                 caps: Object.values(WS) },
  { scope: 'ws',  group: 'CRM',                       caps: Object.values(CRM) },
  { scope: 'ws',  group: 'Workspace Analytics',       caps: [ANALYTICS.CRM_BASIC, ANALYTICS.CRM_ADVANCED] },
  { scope: 'ws',  group: 'AI Assistant',              caps: [AI.COPILOT_USE, AI.EMAIL_ASSIST] },
];

/* CAP_LABELS and CAP_GROUP_LABELS are imported from capabilities-meta.js */

/* ── Permission descriptions (shown in wizard picker — real cap keys only) */
const PERM_DESC = {
  'org.members.view':        'View the list of org members and their profiles',
  'org.members.invite':      'Send membership invitations via email',
  'org.members.edit':        'Edit member profiles, roles, and workspace assignments',
  'org.members.remove':      'Remove members from the organization',
  'org.roles.view':          'View custom roles and their permission details',
  'org.roles.create':        'Create new custom org and workspace roles',
  'org.roles.edit':          'Modify permissions on existing custom roles',
  'org.roles.delete':        'Delete custom roles from the organization',
  'org.workspaces.view':     'View all workspaces and their membership',
  'org.workspaces.create':   'Provision new workspaces in the organization',
  'org.workspaces.edit':     'Rename and reconfigure existing workspaces',
  'org.workspaces.delete':   'Permanently remove workspaces',
  'org.settings.view':       'View organization settings and configuration',
  'org.settings.edit':       'Update organization settings, name, and policies',
  'org.branding.view':       'View brand assets and white-label configuration',
  'org.branding.edit':       'Upload logos and edit brand colors',
  'org.whitelabel.configure':'Configure white-label domain and branding',
  'org.audit.view':          'Access organization-level audit logs',
  'org.audit.export':        'Export audit logs to CSV',
  'org.security.view':       'View security policies and posture',
  'org.security.configure':  'Configure MFA enforcement, IP allowlists, and session policies',
  'org.licenses.view':       'View license allocation and usage across the organization',
  'ws.assignments.view':     'View workspace role assignments',
  'ws.assignments.manage':   'Add, remove, and change workspace role assignments',
  'ai.copilot.use':          'Use the AI copilot for suggestions and summaries',
  'ai.email-assist.use':     'Use the AI email writing assistant',
  'ai.agents.configure':     'Configure AI agents and automation rules',
  'ai.models.configure':     'Configure which AI models are available in the workspace',
};

/* ── Shared: Role Wizard Modal (3-step: Basic Info → Permissions → Review) ── */
function RoleWizardModal({ orgId, wizard, onClose, onSaved, asPage = false }) {
  const toast = useToast();
  const isEdit  = !!(wizard?.role?.id);
  const isClone = !isEdit && !!(wizard?.role?.name);
  const seedRole = wizard?.role ?? null;

  const [step, setStep] = useState(1);
  const [name, setName] = useState(seedRole?.name ?? '');
  const [desc, setDesc] = useState(seedRole?.description ?? seedRole?.desc ?? '');
  const [selected, setSelected] = useState(() => new Set(seedRole?.permissions ?? seedRole?.caps ?? []));
  const [capsSearch, setCapsSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [saving, setSaving] = useState(false);

  const scope = wizard?.scope ?? 'ORG';
  const scopeLabel = scope === 'WORKSPACE' ? 'Workspace' : scope === 'TEMPLATE' ? 'Template' : 'Organization';
  const scopeTone  = scope === 'WORKSPACE' ? 'blue' : scope === 'TEMPLATE' ? 'purple' : 'green';

  const initialSet        = useMemo(() => new Set(seedRole?.permissions ?? seedRole?.caps ?? []), [seedRole]);
  const addedCaps         = useMemo(() => [...selected].filter(c => !initialSet.has(c)), [selected, initialSet]);
  const removedCaps       = useMemo(() => [...initialSet].filter(c => !selected.has(c)), [selected, initialSet]);
  const selectedAreaCount = useMemo(() => new Set([...selected].map(c => c.split('.')[0])).size, [selected]);

  const toggle = (cap) => setSelected(prev => {
    const s = new Set(prev);
    s.has(cap) ? s.delete(cap) : s.add(cap);
    return s;
  });

  const toggleGroup = (keys) => {
    const allOn = keys.every(k => selected.has(k));
    setSelected(prev => {
      const s = new Set(prev);
      if (allOn) keys.forEach(k => s.delete(k));
      else keys.forEach(k => s.add(k));
      return s;
    });
  };

  const clearGroup = (keys) => {
    setSelected(prev => {
      const s = new Set(prev);
      keys.forEach(k => s.delete(k));
      return s;
    });
  };

  const toggleCollapse = (grpKey) => setCollapsed(prev => ({ ...prev, [grpKey]: !prev[grpKey] }));

  const filteredGroups = useMemo(() => {
    const q = capsSearch.toLowerCase();
    const scopedCatalog = scope === 'TEMPLATE'
      ? CATALOG_GROUPS
      : CATALOG_GROUPS.filter(g => g.scope === (scope === 'ORG' ? 'org' : 'ws'));
    return scopedCatalog.map(grp => ({
      ...grp,
      caps: grp.caps.filter(cap =>
        !q || cap.includes(q) || (CAP_LABELS[cap] ?? cap).toLowerCase().includes(q) ||
        (PERM_DESC[cap] ?? '').toLowerCase().includes(q)
      ),
    })).filter(grp => grp.caps.length > 0);
  }, [capsSearch, scope]);

  const step1Valid = name.trim().length >= 2;
  const step2Valid = selected.size > 0;

  const handleSave = async () => {
    setSaving(true);
    try {
      const body = {
        name: name.trim(),
        description: desc.trim(),
        permissions: [...selected],
        scope: scope === 'TEMPLATE' ? undefined : scope.toLowerCase(),
      };
      if (isEdit) {
        await orgRolesApi.update(orgId, seedRole.id, body);
        toast({ title: `Role "${body.name}" updated`, tone: 'success' });
      } else if (scope === 'TEMPLATE') {
        await orgTemplatesApi.create(orgId, { name: body.name, description: body.description, config: { permissions: body.permissions } });
        toast({ title: `Template "${body.name}" created`, tone: 'success' });
      } else {
        await orgRolesApi.create(orgId, body);
        toast({ title: `Role "${body.name}" created`, tone: 'success' });
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast({ title: e?.message || 'Save failed', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const modeLabel = isEdit ? 'Edit Role' : isClone ? 'Clone Role' : `New ${scopeLabel} Role`;
  const steps = ['Basic Info', 'Permissions', 'Review'];

  return (
    <div
      className={asPage ? '' : 'nx-modal-overlay'}
      onClick={asPage ? undefined : onClose}
      style={asPage ? {} : { zIndex: 1200 }}
    >
      <div
        className={asPage ? '' : 'nx-modal'}
        style={asPage
          ? { background: 'var(--nx-surface)', borderRadius: 10, border: '1px solid var(--nx-border)', display: 'flex', flexDirection: 'column' }
          : { width: 640, maxWidth: '94vw', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }
        }
        onClick={asPage ? undefined : e => e.stopPropagation()}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px 0', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <NexusIcon name="Shield" size={18} style={{ color: 'var(--nx-accent)' }} />
              <span style={{ fontWeight: 700, fontSize: 15, color: 'var(--nx-text)' }}>{modeLabel}</span>
              <Pill tone={scopeTone} style={{ fontSize: 10 }}>{scopeLabel}</Pill>
            </div>
            <button className="nx-icon-btn" onClick={onClose}><NexusIcon name="X" size={16} /></button>
          </div>
          {/* Step indicator */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 16 }}>
            {steps.map((s, i) => {
              const num = i + 1;
              const active = step === num;
              const done = step > num;
              return (
                <React.Fragment key={s}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <div style={{
                      width: 24, height: 24, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: 11, fontWeight: 700, flexShrink: 0,
                      background: done ? 'var(--nx-accent)' : active ? 'color-mix(in srgb, var(--nx-accent) 15%, transparent)' : 'var(--nx-surface-raised, var(--nx-border))',
                      color: done ? '#fff' : active ? 'var(--nx-accent)' : 'var(--nx-text-muted)',
                      border: active ? '2px solid var(--nx-accent)' : done ? 'none' : '1px solid var(--nx-border)',
                    }}>
                      {done ? <NexusIcon name="Check" size={10} style={{ color: '#fff' }} /> : num}
                    </div>
                    <span style={{ fontSize: 11, fontWeight: active ? 600 : 400, color: active ? 'var(--nx-text)' : 'var(--nx-text-muted)', whiteSpace: 'nowrap' }}>{s}</span>
                  </div>
                  {i < steps.length - 1 && (
                    <div style={{ flex: 1, height: 1, background: done ? 'var(--nx-accent)' : 'var(--nx-border)', margin: '0 8px' }} />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div style={{ height: 1, background: 'var(--nx-border)', margin: '0 -20px' }} />
        </div>

        {/* Body */}
        <div style={asPage ? { padding: '16px 20px' } : { flex: 1, overflowY: 'auto', padding: '16px 20px' }}>

          {/* ── Step 1: Basic Info ── */}
          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label className="nx-label" style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600 }}>Role Name <span style={{ color: 'var(--nx-danger, #ef4444)' }}>*</span></label>
                <input
                  className="nx-input"
                  style={{ width: '100%' }}
                  value={name}
                  onChange={e => setName(e.target.value)}
                  placeholder="e.g. Sales Manager"
                  autoFocus
                />
                {name.trim().length > 0 && name.trim().length < 2 && (
                  <p style={{ margin: '4px 0 0', fontSize: 11, color: 'var(--nx-danger, #ef4444)' }}>Name must be at least 2 characters</p>
                )}
              </div>
              <div>
                <label className="nx-label" style={{ display: 'block', marginBottom: 4, fontSize: 12, fontWeight: 600 }}>Description</label>
                <textarea
                  className="nx-input"
                  style={{ width: '100%', minHeight: 72, resize: 'vertical' }}
                  value={desc}
                  onChange={e => setDesc(e.target.value)}
                  placeholder="What is this role for? Who uses it?"
                />
              </div>
              {/* Scope confirmation */}
              <div style={{ padding: '10px 14px', borderRadius: 8, background: 'color-mix(in srgb, var(--nx-border) 40%, transparent)', display: 'flex', alignItems: 'center', gap: 10 }}>
                <NexusIcon name="Info" size={14} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
                <div>
                  <p style={{ margin: 0, fontSize: 12, fontWeight: 600, color: 'var(--nx-text)' }}>{scopeLabel} Role</p>
                  <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--nx-text-muted)' }}>
                    {scope === 'WORKSPACE'
                      ? 'Applied within a specific workspace. Controls access to workspace data and members.'
                      : scope === 'TEMPLATE'
                      ? 'Reusable template. Can be applied to create org or workspace roles quickly.'
                      : 'Applied across the organization. Controls access to org-level settings and members.'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* ── Step 2: Permissions ── */}
          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <input
                  className="nx-input nx-input--inline"
                  placeholder="Search permissions..."
                  value={capsSearch}
                  onChange={e => setCapsSearch(e.target.value)}
                  style={{ flex: 1 }}
                />
                <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                  {selected.size} selected
                </span>
              </div>

              {filteredGroups.length === 0 && (
                <p style={{ textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13, padding: '24px 0' }}>No permissions match your search</p>
              )}

              {filteredGroups.map(grp => {
                const grpSelected = grp.caps.filter(c => selected.has(c)).length;
                const allSelected = grpSelected === grp.caps.length;
                const isCollapsed = collapsed[grp.group];

                return (
                  <div key={grp.group} style={{ border: '1px solid var(--nx-border)', borderRadius: 8, overflow: 'hidden' }}>
                    {/* Group header */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'color-mix(in srgb, var(--nx-border) 30%, transparent)', cursor: 'pointer' }}
                      onClick={() => toggleCollapse(grp.group)}>
                      <NexusIcon name={isCollapsed ? 'ChevronRight' : 'ChevronDown'} size={13} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
                      <span style={{ fontWeight: 600, fontSize: 12, color: 'var(--nx-text)', flex: 1 }}>
                        {grp.group}
                      </span>
                      <span style={{ fontSize: 11, color: grpSelected > 0 ? 'var(--nx-accent)' : 'var(--nx-text-muted)' }}>
                        {grpSelected}/{grp.caps.length}
                      </span>
                      {!isCollapsed && (
                        <>
                          <button
                            className="nx-icon-btn"
                            style={{ fontSize: 10, padding: '2px 6px', height: 'auto' }}
                            title={allSelected ? 'Deselect all' : 'Select all'}
                            onClick={e => { e.stopPropagation(); toggleGroup(grp.caps); }}
                          >
                            {allSelected ? 'Deselect all' : 'Select all'}
                          </button>
                          {grpSelected > 0 && (
                            <button
                              className="nx-icon-btn"
                              style={{ fontSize: 10, padding: '2px 6px', height: 'auto', color: 'var(--nx-danger, #ef4444)' }}
                              title="Clear group"
                              onClick={e => { e.stopPropagation(); clearGroup(grp.caps); }}
                            >
                              Clear
                            </button>
                          )}
                        </>
                      )}
                    </div>

                    {/* Group permissions */}
                    {!isCollapsed && (
                      <div style={{ padding: '4px 0' }}>
                        {grp.caps.map(cap => (
                          <label key={cap} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '7px 14px', cursor: 'pointer' }}
                            className="nx-iam-perm-row">
                            <input
                              type="checkbox"
                              checked={selected.has(cap)}
                              onChange={() => toggle(cap)}
                              style={{ marginTop: 2, flexShrink: 0 }}
                            />
                            <div style={{ flex: 1, minWidth: 0 }}>
                              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text)' }}>{CAP_LABELS[cap] ?? cap}</div>
                              {PERM_DESC[cap] && (
                                <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 1 }}>{PERM_DESC[cap]}</div>
                              )}
                            </div>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
              {selected.size === 0 && filteredGroups.length > 0 && (
                <p style={{ margin: 0, fontSize: 12, color: 'var(--nx-danger, #ef4444)', textAlign: 'center', padding: '8px 0' }}>
                  At least one permission must be selected to continue.
                </p>
              )}
            </div>
          )}

          {/* ── Step 3: Review ── */}
          {step === 3 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Role summary */}
              <div style={{ padding: '14px 16px', borderRadius: 8, border: '1px solid var(--nx-border)', background: 'color-mix(in srgb, var(--nx-border) 20%, transparent)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <NexusIcon name="Shield" size={15} style={{ color: 'var(--nx-accent)' }} />
                  <span style={{ fontWeight: 700, fontSize: 14 }}>{name}</span>
                  <Pill tone={scopeTone} style={{ fontSize: 10 }}>{scopeLabel}</Pill>
                </div>
                {desc && <p style={{ margin: 0, fontSize: 12, color: 'var(--nx-text-muted)' }}>{desc}</p>}
              </div>

              {/* Change summary (edit mode only) */}
              {isEdit && (addedCaps.length > 0 || removedCaps.length > 0) && (
                <div style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid color-mix(in srgb, var(--nx-accent) 25%, transparent)', background: 'color-mix(in srgb, var(--nx-accent) 5%, transparent)' }}>
                  <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)' }}>Changes from current version</p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {addedCaps.length > 0 && <Pill tone="green">+{addedCaps.length} added</Pill>}
                    {removedCaps.length > 0 && <Pill tone="red">−{removedCaps.length} removed</Pill>}
                  </div>
                  {addedCaps.length > 0 && (
                    <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {addedCaps.map(c => (
                        <span key={c} className="nx-iam-cap-badge" style={{ background: 'color-mix(in srgb, #22c55e 12%, transparent)', borderColor: '#22c55e' }}>{CAP_LABELS[c] ?? c}</span>
                      ))}
                    </div>
                  )}
                  {removedCaps.length > 0 && (
                    <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {removedCaps.map(c => (
                        <span key={c} className="nx-iam-cap-badge" style={{ background: 'color-mix(in srgb, #ef4444 12%, transparent)', borderColor: '#ef4444', textDecoration: 'line-through' }}>{CAP_LABELS[c] ?? c}</span>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Permission summary grouped */}
              {selected.size === 0 ? (
                <div style={{ padding: '10px 14px', borderRadius: 8, border: '1px solid var(--nx-border)', textAlign: 'center' }}>
                  <p style={{ margin: 0, fontSize: 12, color: 'var(--nx-text-muted)' }}>No permissions selected — this role will have no access.</p>
                </div>
              ) : (
                <div>
                  <p style={{ margin: '0 0 8px', fontSize: 12, fontWeight: 600, color: 'var(--nx-text)' }}>
                    {selected.size} permission{selected.size !== 1 ? 's' : ''} across {selectedAreaCount} area{selectedAreaCount !== 1 ? 's' : ''}
                  </p>
                  {(scope === 'TEMPLATE' ? CATALOG_GROUPS : CATALOG_GROUPS.filter(g => g.scope === (scope === 'ORG' ? 'org' : 'ws'))).map(grp => {
                    const grpCaps = grp.caps.filter(c => selected.has(c));
                    if (grpCaps.length === 0) return null;
                    return (
                      <div key={grp.group} style={{ marginBottom: 8 }}>
                        <p style={{ margin: '0 0 4px', fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                          {grp.group}
                        </p>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                          {grpCaps.map(c => (
                            <span key={c} className="nx-iam-cap-badge">{CAP_LABELS[c] ?? c}</span>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 20px', borderTop: '1px solid var(--nx-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            {step > 1 && (
              <Btn size="sm" variant="ghost" onClick={() => setStep(s => s - 1)}>
                <NexusIcon name="ChevronLeft" size={13} /> Back
              </Btn>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn size="sm" variant="ghost" onClick={onClose}>Cancel</Btn>
            {step < 3 && (
              <Btn size="sm" disabled={(step === 1 && !step1Valid) || (step === 2 && !step2Valid)} onClick={() => setStep(s => s + 1)}>
                Next <NexusIcon name="ChevronRight" size={13} />
              </Btn>
            )}
            {step === 3 && (
              <Btn size="sm" disabled={saving} onClick={handleSave}>
                {isEdit ? 'Save Changes' : 'Create Role'}
              </Btn>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── Shared: Role Detail Drawer ───────────────────────────────────────── */
function RoleDetailDrawer({ orgId, role, onClose, onEdit, onClone, onDelete }) {
  const [detail, setDetail] = useState(null);
  const [auditLog, setAuditLog] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!role?.id) return;
    setLoading(true);
    Promise.all([
      orgRolesApi.get(orgId, role.id).catch(() => null),
      listOrgAudit(orgId, { cats: 'IAM,Org', pageSize: 5 }).catch(() => ({ items: [] })),
    ]).then(([d, a]) => {
      setDetail(d ? { ...d, scope: (d.scope ?? 'org').toUpperCase() } : role);
      setAuditLog(a?.items ?? a?.data ?? []);
    }).finally(() => setLoading(false));
  }, [orgId, role?.id]);

  const r = detail ?? role;
  const isSystem   = r?.type === 'SYSTEM' || r?.isSystem;
  const isTemplate = !!(role?._isTemplate);
  const perms = r?.permissions ?? r?.caps ?? [];

  return (
    <div className="nx-iam-drawer-overlay" onClick={onClose} style={{ zIndex: 1150 }}>
      <div className="nx-iam-drawer" style={{ width: 520 }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="nx-iam-drawer-head">
          <NexusIcon name="Shield" size={16} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 0 }}>
            <p style={{ margin: 0, fontWeight: 700, fontSize: 14, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r?.name}</p>
            <div style={{ display: 'flex', gap: 6, marginTop: 3 }}>
              {isTemplate
                ? <Pill tone="purple" style={{ fontSize: 10 }}>Template</Pill>
                : r?.scope === 'WORKSPACE'
                  ? <Pill tone="blue" style={{ fontSize: 10 }}>Workspace</Pill>
                  : <Pill tone="green" style={{ fontSize: 10 }}>Organization</Pill>
              }
              {r?.type === 'CUSTOM' && <Pill tone="purple" style={{ fontSize: 10 }}>Custom</Pill>}
              {isSystem && <Pill tone="gray" style={{ fontSize: 10 }}>System</Pill>}
            </div>
          </div>
          <button className="nx-icon-btn" onClick={onClose}><NexusIcon name="X" size={16} /></button>
        </div>

        {/* Body */}
        <div className="nx-iam-drawer-body">
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <NexusIcon name="Loader2" size={24} style={{ color: 'var(--nx-text-muted)', animation: 'spin 1s linear infinite' }} />
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {/* Description */}
              {r?.description && (
                <p style={{ margin: 0, fontSize: 13, color: 'var(--nx-text-muted)', lineHeight: 1.5 }}>{r.description}</p>
              )}

              {/* Usage */}
              <div className="nx-card" style={{ padding: '12px 14px' }}>
                <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--nx-text-muted)' }}>Usage</p>
                <div style={{ display: 'flex', gap: 20 }}>
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--nx-text)' }}>{r?.memberCount ?? r?._count?.members ?? 0}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--nx-text-muted)' }}>Members</p>
                  </div>
                  <div style={{ width: 1, background: 'var(--nx-border)' }} />
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--nx-text)' }}>{r?.workspaceCount ?? r?._count?.workspaces ?? 0}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--nx-text-muted)' }}>Workspaces</p>
                  </div>
                  <div style={{ width: 1, background: 'var(--nx-border)' }} />
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ margin: 0, fontSize: 22, fontWeight: 700, color: 'var(--nx-text)' }}>{perms.length}</p>
                    <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--nx-text-muted)' }}>Permissions</p>
                  </div>
                </div>
              </div>

              {/* Metadata */}
              <div className="nx-card" style={{ padding: '12px 14px' }}>
                <p style={{ margin: '0 0 8px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--nx-text-muted)' }}>Metadata</p>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 5 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <span style={{ color: 'var(--nx-text-muted)' }}>Created</span>
                    <span style={{ color: 'var(--nx-text)' }}>{fmtTs(r?.createdAt) || '—'}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                    <span style={{ color: 'var(--nx-text-muted)' }}>Last updated</span>
                    <span style={{ color: 'var(--nx-text)' }}>{fmtTs(r?.updatedAt ?? r?.createdAt) || '—'}</span>
                  </div>
                  {r?.createdBy && (
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12 }}>
                      <span style={{ color: 'var(--nx-text-muted)' }}>Created by</span>
                      <span style={{ color: 'var(--nx-text)' }}>{r.createdBy}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Permissions */}
              {perms.length > 0 && (
                <div className="nx-card" style={{ padding: '12px 14px' }}>
                  <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--nx-text-muted)' }}>
                    Permissions ({perms.length})
                  </p>
                  {CATALOG_GROUPS.map(grp => {
                    const grpCaps = grp.caps.filter(c => perms.includes(c));
                    if (grpCaps.length === 0) return null;
                    return (
                      <div key={grp.group} style={{ marginBottom: 10 }}>
                        <p style={{ margin: '0 0 5px', fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--nx-text-muted)' }}>
                          {grp.group}
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                          {grpCaps.map(cap => (
                            <div key={cap} style={{ display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                              <NexusIcon name="Check" size={12} style={{ color: 'var(--nx-accent)', marginTop: 2, flexShrink: 0 }} />
                              <div>
                                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--nx-text)' }}>{CAP_LABELS[cap] ?? cap}</span>
                                {PERM_DESC[cap] && (
                                  <p style={{ margin: '1px 0 0', fontSize: 11, color: 'var(--nx-text-muted)' }}>{PERM_DESC[cap]}</p>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Recent IAM Activity */}
              {auditLog.length > 0 && (
                <div className="nx-card" style={{ padding: '12px 14px' }}>
                  <p style={{ margin: '0 0 10px', fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--nx-text-muted)' }}>Recent Activity</p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {auditLog.map((ev, i) => (
                      <div key={ev.id ?? i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                        <div style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--nx-accent)', marginTop: 5, flexShrink: 0 }} />
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <p style={{ margin: 0, fontSize: 12, color: 'var(--nx-text)', fontWeight: 500 }}>{ev.action ?? ev.event ?? ev.cat}</p>
                          {ev.actor && <p style={{ margin: '1px 0 0', fontSize: 11, color: 'var(--nx-text-muted)' }}>by {ev.actor}</p>}
                        </div>
                        <span style={{ fontSize: 10, color: 'var(--nx-text-muted)', whiteSpace: 'nowrap', flexShrink: 0 }}>{fmtTs(ev.createdAt ?? ev.ts)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {!isSystem && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--nx-border)', display: 'flex', gap: 8, flexShrink: 0 }}>
            {!isTemplate && (
              <Btn size="sm" onClick={() => { onClose(); onEdit?.(r); }}><NexusIcon name="Pencil" size={13} /> Edit</Btn>
            )}
            <Btn size="sm" variant="ghost" onClick={() => { onClose(); onClone?.(r); }}><NexusIcon name="Copy" size={13} /> Clone</Btn>
            <div style={{ flex: 1 }} />
            <Btn size="sm" variant="danger" onClick={() => { onClose(); onDelete?.(r); }}><NexusIcon name="Trash2" size={13} /> Delete</Btn>
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Segmented control (module-scope — avoid remount-per-render) ────── */
function SegGroup({ value, onChange, options }) {
  return (
    <div className="nx-iam-seg-group">
      {options.map(o => (
        <button
          key={o.value}
          className={`nx-filter-pill${value === o.value ? ' is-active' : ''}`}
          onClick={() => onChange(o.value)}
          style={{ whiteSpace: 'nowrap' }}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/* ── Tab: Roles (unified — Org Roles + WS Roles + Templates) ────────── */
function RolesTab({ orgId }) {
  const toast     = useToast();
  const perm      = usePermissions();
  const canManage = perm.hasAny([ORG.ROLES_CREATE, ORG.ROLES_EDIT, ORG.ROLES_DELETE]) || perm.isSA;

  /* ── server data ──────────────────────────────────────────────────── */
  const [roles,     setRoles]    = useState([]);
  const [templates, setTemplates]= useState([]);
  const [loading,   setLoading]  = useState(true);
  const [error,     setError]    = useState(null);

  /* ── list-view filters (only active when pageMode === 'LIST') ─────── */
  const [roleScope,  setRoleScope]  = useState('all'); // all | org | workspace | templates
  const [typeFilter, setTypeFilter] = useState('all'); // all | custom
  const [search,     setSearch]     = useState('');
  const [sortBy,     setSortBy]     = useState('name');
  const [sortDir,    setSortDir]    = useState('asc');
  const [page,       setPage]       = useState(1);

  /* ── SINGLE SOURCE OF TRUTH: page mode ───────────────────────────── */
  // LIST | CREATE_ORG_ROLE | CREATE_WS_ROLE | CREATE_TEMPLATE | EDIT_ROLE | CLONE_ROLE
  const [pageMode,   setPageMode]   = useState('LIST');
  const [editTarget, setEditTarget] = useState(null);

  /* ── overlays — only mount when pageMode === 'LIST' ──────────────── */
  const [detailRole, setDetailRole] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [deleting,   setDeleting]   = useState(false);

  /* ── data ─────────────────────────────────────────────────────────── */
  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const [rs, ts] = await Promise.all([orgRolesApi.list(orgId), orgTemplatesApi.list(orgId)]);
      const normScope = (r) => ({ ...r, scope: (r.scope ?? 'org').toUpperCase() });
      const normTemplate = (t) => ({ ...t, _isTemplate: true, permissions: t.config?.permissions ?? [] });
      setRoles((rs?.items ?? rs?.data ?? rs ?? []).map(normScope));
      setTemplates((ts?.items ?? ts?.data ?? ts ?? []).map(normTemplate));
    } catch (e) {
      setError(e?.message || 'Failed to load roles');
    } finally { setLoading(false); }
  }, [orgId]);

  useEffect(() => { load(); }, [load]);

  /* reset page when any list-view filter changes */
  useEffect(() => { setPage(1); }, [roleScope, typeFilter, search, sortBy, sortDir]);

  /* ── page mode transitions ────────────────────────────────────────── */
  const enterCreate = useCallback((scope) => {
    setDetailRole(null);
    setEditTarget(null);
    if (scope === 'ORG')            setPageMode('CREATE_ORG_ROLE');
    else if (scope === 'WORKSPACE') setPageMode('CREATE_WS_ROLE');
    else                            setPageMode('CREATE_TEMPLATE');
  }, []);

  const enterEdit = useCallback((r) => {
    setDetailRole(null);
    setEditTarget(r);
    setPageMode('EDIT_ROLE');
  }, []);

  const enterClone = useCallback((r) => {
    setDetailRole(null);
    setEditTarget({ ...r, id: undefined, name: `Copy of ${r.name}` });
    setPageMode('CLONE_ROLE');
  }, []);

  const returnToList = useCallback(() => {
    setPageMode('LIST');
    setEditTarget(null);
  }, []);

  /* after a save: return to LIST and jump scope to what was just created */
  const handleWizardSaved = useCallback(() => {
    if (pageMode === 'CREATE_ORG_ROLE')  setRoleScope('org');
    else if (pageMode === 'CREATE_WS_ROLE')   setRoleScope('workspace');
    else if (pageMode === 'CREATE_TEMPLATE')  setRoleScope('templates');
    else if (pageMode === 'CLONE_ROLE' && editTarget?._isTemplate) setRoleScope('templates');
    setPageMode('LIST');
    setEditTarget(null);
    load();
  }, [pageMode, editTarget, load]);

  /* wizard config — single object derived from pageMode + editTarget */
  const wizardConfig = useMemo(() => {
    if (pageMode === 'CREATE_ORG_ROLE') return { scope: 'ORG',       role: null };
    if (pageMode === 'CREATE_WS_ROLE')  return { scope: 'WORKSPACE',  role: null };
    if (pageMode === 'CREATE_TEMPLATE') return { scope: 'TEMPLATE',   role: null };
    if (pageMode === 'EDIT_ROLE')       return { scope: editTarget?.scope ?? 'ORG', role: editTarget };
    if (pageMode === 'CLONE_ROLE') {
      const cloneScope = editTarget?._isTemplate ? 'TEMPLATE' : (editTarget?.scope ?? 'ORG');
      return { scope: cloneScope, role: editTarget };
    }
    return null;
  }, [pageMode, editTarget]);

  /* ── derived list ─────────────────────────────────────────────────── */
  const displayed = useMemo(() => {
    let list = roleScope === 'templates' ? templates : roles;
    if (roleScope === 'org')       list = list.filter(r => r.scope !== 'WORKSPACE');
    if (roleScope === 'workspace') list = list.filter(r => r.scope === 'WORKSPACE');
    if (typeFilter === 'custom')   list = list.filter(r => r.type !== 'SYSTEM' && !r.isSystem);
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r => {
        if ((r.name ?? '').toLowerCase().includes(q)) return true;
        if ((r.description ?? r.desc ?? '').toLowerCase().includes(q)) return true;
        return (r.permissions ?? r.caps ?? []).some(cap => {
          const label = CAP_LABELS[cap] ?? '';
          return label.toLowerCase().includes(q);
        });
      });
    }
    return [...list].sort((a, b) => {
      let va, vb;
      if (sortBy === 'memberCount') {
        va = a.memberCount ?? a._count?.members ?? 0;
        vb = b.memberCount ?? b._count?.members ?? 0;
      } else if (sortBy === 'permCount') {
        va = (a.permissions ?? a.caps ?? []).length;
        vb = (b.permissions ?? b.caps ?? []).length;
      } else {
        va = a[sortBy] ?? '';
        vb = b[sortBy] ?? '';
        if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb + '').toLowerCase(); }
      }
      return sortDir === 'asc' ? (va > vb ? 1 : -1) : (va < vb ? 1 : -1);
    });
  }, [roles, templates, roleScope, typeFilter, search, sortBy, sortDir]);

  const PAGE_SIZE  = 20;
  const totalPages = Math.max(1, Math.ceil(displayed.length / PAGE_SIZE));
  const pagedRoles = displayed.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const customRoleCount = useMemo(() =>
    (roleScope === 'templates' ? templates : roles)
      .filter(r => r.type !== 'SYSTEM' && !r.isSystem).length,
    [roles, templates, roleScope]
  );

  /* ── actions ─────────────────────────────────────────────────────── */
  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir('asc'); }
  };

  const handleDelete = async () => {
    if (!confirmDel) return;
    setDeleting(true);
    try {
      if (confirmDel._isTemplate) {
        await orgTemplatesApi.remove(orgId, confirmDel.id);
      } else {
        await orgRolesApi.remove(orgId, confirmDel.id);
      }
      toast({ title: `"${confirmDel.name}" deleted`, tone: 'success' });
      setConfirmDel(null);
      load();
    } catch (e) {
      toast({ title: e?.message || 'Delete failed', tone: 'error' });
    } finally { setDeleting(false); }
  };

  /* ── filter options ─────────────────────────────────────────────── */
  const scopeOptions = [
    { value: 'all',       label: 'All'       },
    { value: 'org',       label: 'Org'       },
    { value: 'workspace', label: 'Workspace' },
    { value: 'templates', label: 'Templates' },
  ];
  const typeOptions = [
    { value: 'all',    label: 'All'    },
    { value: 'custom', label: 'Custom' },
  ];

  /* ── plain helpers (NOT React components — avoids remount each render) */
  const sortIcon = (col) => sortBy === col
    ? <NexusIcon name={sortDir === 'asc' ? 'ChevronUp' : 'ChevronDown'} size={11} style={{ color: 'var(--nx-accent)', marginLeft: 3 }} />
    : null;

  const colCount = canManage ? 8 : 7;

  const renderEmptyState = (label, createScope) => (
    <tr>
      <td colSpan={colCount} style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
          <NexusIcon name="Shield" size={28} style={{ color: 'var(--nx-text-muted)' }} />
          <p style={{ margin: 0, fontWeight: 600, fontSize: 14 }}>No {label} yet</p>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--nx-text-muted)' }}>
            {createScope === 'TEMPLATE'
              ? 'Templates are reusable blueprints for quickly creating new roles.'
              : 'Custom roles give members precisely scoped access to your organization.'}
          </p>
          {canManage && createScope && (
            <Btn size="sm" onClick={() => enterCreate(createScope)}>
              <NexusIcon name="Plus" size={13} /> Create {createScope === 'TEMPLATE' ? 'Template' : createScope === 'WORKSPACE' ? 'Workspace Role' : 'Organization Role'}
            </Btn>
          )}
        </div>
      </td>
    </tr>
  );

  /* ══════════════════════════════════════════════════════════════════
     PAGE MODE ≠ LIST → wizard replaces entire page content (Rule 1-4)
  ══════════════════════════════════════════════════════════════════ */
  if (pageMode !== 'LIST' && wizardConfig) {
    const wizardTitle =
      pageMode === 'EDIT_ROLE'  ? `Edit: ${editTarget?.name}`  :
      pageMode === 'CLONE_ROLE' ? `Clone: ${editTarget?.name}` :
      `New ${wizardConfig.scope === 'WORKSPACE' ? 'Workspace' : wizardConfig.scope === 'TEMPLATE' ? 'Template' : 'Organization'} Role`;

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {/* Breadcrumb nav back to list */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button
            className="nx-icon-btn"
            onClick={returnToList}
            title="Back to Roles"
            style={{ flexShrink: 0 }}
          >
            <NexusIcon name="ArrowLeft" size={14} />
          </button>
          <span
            style={{ fontSize: 12, color: 'var(--nx-accent)', cursor: 'pointer' }}
            onClick={returnToList}
          >
            Roles
          </span>
          <NexusIcon name="ChevronRight" size={11} style={{ color: 'var(--nx-text-muted)' }} />
          <span style={{ fontSize: 12, color: 'var(--nx-text)' }}>{wizardTitle}</span>
        </div>

        {/* Wizard as inline page content — no modal overlay */}
        <RoleWizardModal
          orgId={orgId}
          wizard={wizardConfig}
          onClose={returnToList}
          onSaved={handleWizardSaved}
          asPage
        />
      </div>
    );
  }

  /* ══════════════════════════════════════════════════════════════════
     PAGE MODE: LIST
  ══════════════════════════════════════════════════════════════════ */
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

      {/* ── Toolbar ───────────────────────────────────────────────── */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>

        {/* Left: title + scope seg + type seg (hide type seg for templates) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, flexWrap: 'wrap' }}>
          <span style={{ fontWeight: 700, fontSize: 14, color: 'var(--nx-text)', whiteSpace: 'nowrap' }}>
            Roles
          </span>
          <SegGroup
            value={roleScope}
            onChange={v => { setRoleScope(v); setTypeFilter('all'); setSearch(''); setPage(1); }}
            options={scopeOptions}
          />
          {roleScope !== 'templates' && (
            <SegGroup value={typeFilter} onChange={setTypeFilter} options={typeOptions} />
          )}
        </div>

        <div style={{ flex: 1 }} />

        {/* Right: search + contextual create buttons (scope-specific per Rules 10-12) */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            className="nx-input nx-input--inline"
            placeholder={roleScope === 'templates' ? 'Search templates...' : 'Search roles...'}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ width: 180 }}
          />
          {canManage && (
            <>
              {(roleScope === 'all' || roleScope === 'org') && (
                <Btn size="sm" variant="ghost" onClick={() => enterCreate('ORG')}>
                  <NexusIcon name="Plus" size={13} /> New Org Role
                </Btn>
              )}
              {(roleScope === 'all' || roleScope === 'workspace') && (
                <Btn size="sm" variant="ghost" onClick={() => enterCreate('WORKSPACE')}>
                  <NexusIcon name="Plus" size={13} /> New WS Role
                </Btn>
              )}
              {(roleScope === 'all' || roleScope === 'templates') && (
                <Btn size="sm" onClick={() => enterCreate('TEMPLATE')}>
                  <NexusIcon name="Plus" size={13} /> New Template
                </Btn>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── Error banner ──────────────────────────────────────────── */}
      {error && (
        <div className="nx-iam-banner nx-iam-banner--error" style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <NexusIcon name="AlertCircle" size={15} />
          <span>{error}</span>
          <button className="nx-icon-btn" style={{ marginLeft: 'auto' }} onClick={load}>
            <NexusIcon name="RefreshCw" size={13} />
          </button>
        </div>
      )}

      {/* ── Table ─────────────────────────────────────────────────── */}
      <div className="nx-card" style={{ overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table className="nx-table" style={{ minWidth: 700 }}>
            <thead>
              <tr>
                <th scope="col" style={{ cursor: 'pointer' }} onClick={() => toggleSort('name')}>
                  Role {sortIcon('name')}
                </th>
                <th scope="col">Scope</th>
                <th scope="col">Type</th>
                <th scope="col" style={{ cursor: 'pointer' }} onClick={() => toggleSort('memberCount')}>
                  Members {sortIcon('memberCount')}
                </th>
                <th scope="col">Workspaces</th>
                <th scope="col" style={{ cursor: 'pointer' }} onClick={() => toggleSort('permCount')}>
                  Permissions {sortIcon('permCount')}
                </th>
                <th scope="col" style={{ cursor: 'pointer' }} onClick={() => toggleSort('updatedAt')}>
                  Last Updated {sortIcon('updatedAt')}
                </th>
                {canManage && <th scope="col" style={{ width: 100 }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={colCount} style={{ padding: '32px 20px', textAlign: 'center' }}>
                    <NexusIcon name="Loader2" size={20} style={{ color: 'var(--nx-text-muted)', animation: 'spin 1s linear infinite' }} />
                  </td>
                </tr>
              ) : pagedRoles.length === 0 ? (
                /* Empty states — scope-specific (Rules 13-14) */
                (search || typeFilter !== 'all') ? (
                  <tr>
                    <td colSpan={colCount} style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13 }}>
                      No roles match your filters.{' '}
                      <button className="nx-link" onClick={() => { setSearch(''); setTypeFilter('all'); }}>
                        Clear filters
                      </button>
                    </td>
                  </tr>
                ) : roleScope === 'templates' ? renderEmptyState('Templates',          'TEMPLATE')
                  : roleScope === 'org'        ? renderEmptyState('Organization Roles', 'ORG')
                  : roleScope === 'workspace'  ? renderEmptyState('Workspace Roles',    'WORKSPACE')
                  : customRoleCount === 0      ? renderEmptyState('Roles',              'ORG')
                  : (
                    <tr>
                      <td colSpan={colCount} style={{ padding: '32px 20px', textAlign: 'center', color: 'var(--nx-text-muted)', fontSize: 13 }}>
                        No roles found.
                      </td>
                    </tr>
                  )
              ) : (
                pagedRoles.map(r => {
                  const isSystem   = r.type === 'SYSTEM' || r.isSystem;
                  const isTemplate = roleScope === 'templates';
                  const perms      = r.permissions ?? r.caps ?? [];
                  return (
                    <tr key={r.id}>
                      <td>
                        <button
                          className="nx-link"
                          style={{ fontWeight: 600, fontSize: 13, textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--nx-accent)', padding: 0 }}
                          onClick={() => setDetailRole({ ...r, _isTemplate: isTemplate })}
                        >
                          {r.name}
                        </button>
                        {r.description && (
                          <p style={{ margin: '2px 0 0', fontSize: 11, color: 'var(--nx-text-muted)', maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {r.description}
                          </p>
                        )}
                      </td>
                      <td>
                        {isTemplate
                          ? <Pill tone="purple" style={{ fontSize: 10 }}>Template</Pill>
                          : r.scope === 'WORKSPACE'
                            ? <Pill tone="blue"  style={{ fontSize: 10 }}>Workspace</Pill>
                            : <Pill tone="green" style={{ fontSize: 10 }}>Organization</Pill>
                        }
                      </td>
                      <td>
                        <Pill tone={isSystem ? 'gray' : 'purple'} style={{ fontSize: 10 }}>
                          {isSystem ? 'System' : 'Custom'}
                        </Pill>
                      </td>
                      <td style={{ fontSize: 12 }}>{r.memberCount ?? r._count?.members ?? '—'}</td>
                      <td style={{ fontSize: 12 }}>{r.workspaceCount ?? r._count?.workspaces ?? '—'}</td>
                      <td style={{ fontSize: 12 }}>{perms.length}</td>
                      <td style={{ fontSize: 11, color: 'var(--nx-text-muted)', whiteSpace: 'nowrap' }}>
                        {fmtTs(r.updatedAt ?? r.createdAt)}
                      </td>
                      {canManage && (
                        <td>
                          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
                            {!isSystem ? (
                              <>
                                {!isTemplate && (
                                  <button
                                    className="nx-icon-btn"
                                    title="Edit"
                                    onClick={() => enterEdit(r)}
                                  >
                                    <NexusIcon name="Pencil" size={12} />
                                  </button>
                                )}
                                <button
                                  className="nx-icon-btn"
                                  title="Clone"
                                  onClick={() => enterClone({ ...r, _isTemplate: isTemplate })}
                                >
                                  <NexusIcon name="Copy" size={12} />
                                </button>
                                <button
                                  className="nx-icon-btn nx-icon-btn--danger"
                                  title="Delete"
                                  onClick={() => setConfirmDel({ ...r, _isTemplate: isTemplate })}
                                >
                                  <NexusIcon name="Trash2" size={12} />
                                </button>
                              </>
                            ) : (
                              <button
                                className="nx-icon-btn"
                                title="View details"
                                onClick={() => setDetailRole(r)}
                              >
                                <NexusIcon name="Eye" size={12} />
                              </button>
                            )}
                          </div>
                        </td>
                      )}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination footer — scoped to current filter (Rules 16-17) */}
        {totalPages > 1 && (
          <div style={{ padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderTop: '1px solid var(--nx-border)' }}>
            <span style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>
              Showing {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, displayed.length)} of {displayed.length}
            </span>
            <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              <button className="nx-icon-btn" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <NexusIcon name="ChevronLeft" size={13} />
              </button>
              <span style={{ fontSize: 12, color: 'var(--nx-text-muted)', minWidth: 40, textAlign: 'center' }}>{page}/{totalPages}</span>
              <button className="nx-icon-btn" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                <NexusIcon name="ChevronRight" size={13} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Templates info tip (only when viewing templates scope) */}
      {roleScope === 'templates' && templates.length > 0 && (
        <div style={{ padding: '10px 14px', borderRadius: 8, background: 'color-mix(in srgb, var(--nx-border) 30%, transparent)', fontSize: 12, color: 'var(--nx-text-muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
          <NexusIcon name="Info" size={13} style={{ flexShrink: 0 }} />
          <span>Templates are role blueprints. Clone a template to create an editable role from it.</span>
        </div>
      )}

      {/* ── Detail drawer — overlays LIST, no page mode change (Rule 5) ── */}
      {detailRole && (
        <RoleDetailDrawer
          orgId={orgId}
          role={detailRole}
          onClose={() => setDetailRole(null)}
          onEdit={enterEdit}
          onClone={enterClone}
          onDelete={r => {
            setDetailRole(null);
            setConfirmDel({ ...r, _isTemplate: roleScope === 'templates' });
          }}
        />
      )}

      {/* ── Delete confirmation dialog ─────────────────────────────── */}
      {confirmDel && (
        <div className="nx-modal-overlay" onClick={() => setConfirmDel(null)} style={{ zIndex: 1300 }}>
          <div className="nx-modal" style={{ maxWidth: 440 }} onClick={e => e.stopPropagation()}>
            <div className="nx-modal-head">
              <NexusIcon name="AlertTriangle" size={16} style={{ color: 'var(--nx-danger, #ef4444)' }} />
              <span style={{ fontWeight: 700, fontSize: 14 }}>Delete Role</span>
            </div>
            <div style={{ padding: '14px 16px' }}>
              <p style={{ margin: '0 0 10px', fontSize: 13, color: 'var(--nx-text)' }}>
                Delete <strong>{confirmDel.name}</strong>?{' '}
                {((confirmDel.memberCount ?? confirmDel._count?.members) > 0) && (
                  <><strong style={{ color: 'var(--nx-danger, #ef4444)' }}>
                    {confirmDel.memberCount ?? confirmDel._count?.members}{' '}
                    member{(confirmDel.memberCount ?? confirmDel._count?.members) !== 1 ? 's' : ''}
                  </strong> will revert to their base org role. </>
                )}
                {((confirmDel.workspaceCount ?? confirmDel._count?.workspaces) > 0) && (
                  <><strong style={{ color: 'var(--nx-warning, #f59e0b)' }}>
                    {confirmDel.workspaceCount ?? confirmDel._count?.workspaces}{' '}
                    workspace{(confirmDel.workspaceCount ?? confirmDel._count?.workspaces) !== 1 ? 's' : ''}
                  </strong> using this role will be affected. </>
                )}
                This cannot be undone.
              </p>
              <div style={{ padding: '8px 12px', borderRadius: 6, background: 'color-mix(in srgb, var(--nx-warning, #f59e0b) 10%, transparent)', border: '1px solid color-mix(in srgb, var(--nx-warning, #f59e0b) 30%, transparent)', fontSize: 12, color: 'var(--nx-text-muted)', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                <NexusIcon name="ClipboardList" size={13} style={{ flexShrink: 0, marginTop: 1 }} />
                <span>This deletion will be recorded in the organization audit log.</span>
              </div>
            </div>
            <div style={{ padding: '10px 16px', borderTop: '1px solid var(--nx-border)', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Btn size="sm" variant="ghost" onClick={() => setConfirmDel(null)}>Cancel</Btn>
              <Btn size="sm" variant="danger" disabled={deleting} onClick={handleDelete}>Delete Role</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


/* ── Panel: Member Assignments (used by AccessTab) ──────────────────── */
function MembersAccessPanel({ orgId, wss, preselectedMemberId, onCheckAccess }) {
  const toast = useToast();
  const perm  = usePermissions();
  const canManage = perm.has(ORG.MEMBERS_EDIT) || perm.isSA;

  const [members,      setMembers]      = useState([]);
  const [search,       setSearch]       = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selected,     setSelected]     = useState(null);
  const [detail,       setDetail]       = useState(null);
  const [memberships, setMemberships] = useState([]);
  const [busy,       setBusy]       = useState(true);
  const [listErr,    setListErr]    = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [detailErr,  setDetailErr]  = useState(null);
  const [wsBusy,     setWsBusy]     = useState({});
  const [showAddWs,  setShowAddWs]  = useState(false);

  useEffect(() => {
    let alive = true;
    membersApi.list(orgId, { pageSize: 200 })
      .then(r => { if (alive) { setMembers(r?.members ?? r?.data ?? r ?? []); setBusy(false); } })
      // MEDIUM-15: surface member list failure instead of showing empty list
      .catch(e => { if (alive) { setListErr(e?.message ?? 'Failed to load members'); setBusy(false); } });
    return () => { alive = false; };
  }, [orgId]);


  const selectMember = useCallback((m) => {
    setSelected(m);
    setDetail(null);
    setMemberships([]);
    setDetailErr(null);
    setDetailBusy(true);
    membersApi.getOne(orgId, m.id)
      .then(r => { setDetail(r); setMemberships(r?.memberships ?? r?.workspaces ?? []); setDetailBusy(false); })
      // MEDIUM-15: surface detail fetch failure
      .catch(e => { setDetailErr(e?.message ?? 'Failed to load member detail'); setDetailBusy(false); });
  }, [orgId]);

  useEffect(() => {
    if (!preselectedMemberId || members.length === 0 || selected) return;
    const match = members.find(m => m.id === preselectedMemberId);
    if (match) selectMember(match);
  }, [preselectedMemberId, members, selected, selectMember]);

  const addToWs = useCallback(async (ws) => {
    if (!selected) return;
    setWsBusy(b => ({ ...b, [ws.id]: true }));
    try {
      const res = await wsApi.addMember(ws.id, { memberId: selected.id, wsRole: 'WORKSPACE_USER' });
      setMemberships(prev => [...prev, { id: res.id, workspaceId: ws.id, wsRole: res.wsRole ?? 'WORKSPACE_USER', workspace: ws }]);
      toast({ title: `Added to ${ws.name}`, tone: 'success' });
      setShowAddWs(false);
    } catch (e) {
      toast({ title: e?.message || 'Failed to add to workspace', tone: 'error' });
    } finally {
      setWsBusy(b => ({ ...b, [ws.id]: false }));
    }
  }, [selected, toast]);

  const removeFromWs = useCallback(async (ms) => {
    if (!selected) return;
    const wsId = ms.workspaceId || ms.wsId;
    setWsBusy(b => ({ ...b, [wsId]: true }));
    try {
      await wsApi.removeMember(wsId, ms.id);
      setMemberships(prev => prev.filter(x => x.id !== ms.id));
      toast({ title: 'Removed from workspace', tone: 'success' });
    } catch (e) {
      toast({ title: e?.message || 'Failed to remove from workspace', tone: 'error' });
    } finally {
      setWsBusy(b => ({ ...b, [wsId]: false }));
    }
  }, [selected, toast]);

  const changeWsRole = useCallback(async (ms, newRole) => {
    if (!selected) return;
    const wsId = ms.workspaceId || ms.wsId;
    setWsBusy(b => ({ ...b, [wsId]: true }));
    try {
      await wsApi.updateMember(wsId, ms.id, { wsRole: newRole });
      setMemberships(prev => prev.map(x => x.id === ms.id ? { ...x, wsRole: newRole } : x));
      toast({ title: 'Role updated', tone: 'success' });
    } catch (e) {
      toast({ title: e?.message || 'Failed to update role', tone: 'error' });
    } finally {
      setWsBusy(b => ({ ...b, [wsId]: false }));
    }
  }, [selected, toast]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return members
      .filter(m => statusFilter === 'all' || (m.status || 'ACTIVE') === statusFilter)
      .filter(m => !q || (m.name || m.user?.name || '').toLowerCase().includes(q) || (m.email || m.user?.email || '').toLowerCase().includes(q));
  }, [members, search, statusFilter]);

  const assignedWsIds = new Set(memberships.map(ms => ms.workspaceId || ms.wsId));
  const availableWss  = wss.filter(w => !assignedWsIds.has(w.id));

  if (busy) return <div className="nx-loading"><div className="nx-spinner" /></div>;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* M-06: CTA when no workspaces exist yet */}
      {wss.length === 0 && (
        <div className="nx-iam-banner nx-iam-banner--warn" style={{ margin: '12px 16px 0' }}>
          <NexusIcon name="AlertTriangle" size={13} style={{ color: '#b45309', flexShrink: 0, marginTop: 1 }} />
          <span>No workspaces exist for this organization yet. Create a workspace first before assigning members to it.</span>
        </div>
      )}
    <div className="nx-iam-split">
      {/* Left: member list */}
      <div className="nx-iam-split-left">
        <div className="nx-iam-search-bar">
          <NexusIcon name="Search" size={13} />
          <input className="nx-input--inline" placeholder="Search members..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="nx-iam-filter-bar">
          {[['all','All'], ['ACTIVE','Active'], ['PENDING','Pending'], ['SUSPENDED','Suspended']].map(([s, lbl]) => (
            <button key={s} className={`nx-filter-pill${statusFilter === s ? ' is-active' : ''}`} onClick={() => setStatusFilter(s)}>
              {lbl}
            </button>
          ))}
        </div>
        <div className="nx-iam-list-head">{filtered.length === members.length ? `${members.length} members` : `${filtered.length} of ${members.length}`}</div>
        <div className="nx-iam-list">
          {listErr && <div className="nx-empty-sm" style={{ color: 'var(--nx-danger, #ef4444)' }}>{listErr}</div>}
          {!listErr && filtered.length === 0 && <div className="nx-empty-sm">No members found</div>}
          {filtered.map(m => {
            const name  = m.name || m.user?.name || m.email || m.user?.email || 'Unknown';
            const email = m.email || m.user?.email || '';
            const role  = m.orgRole || m.role || 'MEMBER';
            return (
              <button key={m.id} className={'nx-iam-member-row' + (selected?.id === m.id ? ' is-selected' : '')} onClick={() => selectMember(m)}>
                <div className="nx-iam-member-avatar">{name[0]?.toUpperCase() ?? '?'}</div>
                <div className="nx-iam-member-info">
                  <span className="nx-iam-member-name">{name}</span>
                  <span className="nx-iam-member-email">{email}</span>
                </div>
                <Pill tone={ROLE_TONE[role] ?? 'slate'} style={{ fontSize: 10 }}>{ROLE_LABEL[role] ?? role}</Pill>
              </button>
            );
          })}
        </div>
      </div>

      {/* Right: workspace assignments */}
      <div className="nx-iam-split-right">
        {!selected && (
          <div className="nx-iam-detail-empty">
            <NexusIcon name="Users" size={28} />
            <span>Select a member to manage their workspace access</span>
          </div>
        )}
        {selected && (
          <>
            <div className="nx-iam-detail-head">
              <div>
                <div className="nx-iam-detail-name">{selected.name || selected.user?.name || selected.email || 'Unknown'}</div>
                <div className="nx-iam-detail-meta">
                  <span className="nx-iam-detail-sub">{selected.email || selected.user?.email || ''}</span>
                  <Pill tone={ROLE_TONE[selected.orgRole ?? selected.role] ?? 'slate'} style={{ fontSize: 10 }}>{ROLE_LABEL[selected.orgRole ?? selected.role] ?? 'MEMBER'}</Pill>
                  <Pill tone={STATUS_TONE[selected.status] ?? 'slate'} style={{ fontSize: 10 }}>{selected.status ?? 'ACTIVE'}</Pill>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                {onCheckAccess && (
                  <Btn size="sm" variant="ghost" onClick={() => onCheckAccess(selected?.id)}>
                    <NexusIcon name="Key" size={13} /> Check Access
                  </Btn>
                )}
                {canManage && (
                  <Btn size="sm" variant="secondary" onClick={() => setShowAddWs(true)} disabled={availableWss.length === 0}>
                    <NexusIcon name="Plus" size={13} /> Add to Workspace
                  </Btn>
                )}
              </div>
            </div>
            {detailBusy && <div className="nx-loading"><div className="nx-spinner" /></div>}
            {!detailBusy && detailErr && (
              <div className="nx-empty" style={{ paddingTop: 40 }}><NexusIcon name="AlertCircle" size={18} /><span>{detailErr}</span></div>
            )}
            {!detailBusy && !detailErr && (
              <>
                <div className="nx-iam-ws-count">{memberships.length} of {wss.length} workspaces assigned</div>
                {memberships.length === 0 && <div className="nx-empty-sm">Not assigned to any workspaces</div>}
                {memberships.map(ms => {
                  const wsId   = ms.workspaceId || ms.wsId;
                  const ws     = ms.workspace || wss.find(w => w.id === wsId) || {};
                  const role   = ms.wsRole || 'WORKSPACE_USER';
                  const isBusy = wsBusy[wsId];
                  return (
                    <div key={ms.id} className="nx-iam-ws-row">
                      <div className="nx-iam-ws-icon"><NexusIcon name="LayoutGrid" size={13} /></div>
                      <div className="nx-iam-ws-name">{ws.name || wsId}</div>
                      {canManage
                        ? <select className="nx-input" style={{ fontSize: 12, height: 28, width: 140 }} value={role} disabled={isBusy} onChange={e => changeWsRole(ms, e.target.value)}>
                            <option value="WORKSPACE_USER">Workspace User</option>
                            <option value="WORKSPACE_ADMIN">Workspace Admin</option>
                          </select>
                        : <Pill tone={WS_ROLE_TONE[role] ?? 'slate'}>{WS_ROLE_LABEL[role] ?? role}</Pill>}
                      {canManage && (
                        <button className="nx-icon-btn nx-icon-btn--danger" disabled={isBusy} onClick={() => removeFromWs(ms)} title="Remove from workspace">
                          <NexusIcon name="Trash2" size={13} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>

      {/* Add to workspace modal */}
      {showAddWs && (
        <div className="nx-modal-overlay" onClick={() => setShowAddWs(false)}>
          <div className="nx-modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="nx-modal-head">
              <span>Add to Workspace</span>
              <button className="nx-modal-close" onClick={() => setShowAddWs(false)}><NexusIcon name="X" size={14} /></button>
            </div>
            <div style={{ padding: '12px 0' }}>
              {availableWss.length === 0
                ? <div className="nx-empty-sm">Already assigned to all workspaces</div>
                : availableWss.map(ws => (
                  <button key={ws.id} className="nx-iam-ws-pick-row" disabled={wsBusy[ws.id]} onClick={() => addToWs(ws)}>
                    <NexusIcon name="LayoutGrid" size={14} />
                    <span>{ws.name}</span>
                    <Pill tone={STATUS_TONE[ws.status] ?? 'slate'} style={{ marginLeft: 'auto' }}>{ws.status}</Pill>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

/* ── Panel: Workspace Access (used by AccessTab) ────────────────────── */
function WorkspaceAccessPanel({ orgId, wss, preselectedWsId }) {
  const toast      = useToast();
  const perm       = usePermissions();
  // HIGH-09: use capability-based check consistently
  const canManage  = perm.has(ORG.MEMBERS_EDIT) || perm.isSA;
  const isOrgAdmin = perm.has(ORG.MEMBERS_EDIT) || perm.isSA;

  const [selected,    setSelected]    = useState(null);
  const [wsMembers,   setWsMembers]   = useState([]);
  const [allMembers,  setAllMembers]  = useState([]);
  const [busy,        setBusy]        = useState(false);
  const [wsErr,       setWsErr]       = useState(null);
  const [actionBusy,  setActionBusy]  = useState({});
  const [showAdd,     setShowAdd]     = useState(false);
  const [addRole,     setAddRole]     = useState('WORKSPACE_USER');
  const [addMemberId, setAddMemberId] = useState('');
  // LOW-13: workspace search filter
  const [wsSearch,    setWsSearch]    = useState('');

  const loadWsMembers = useCallback((ws) => {
    setSelected(ws);
    setWsMembers([]);
    setWsErr(null);
    setBusy(true);
    wsApi.listMembers(ws.id)
      .then(r => { setWsMembers(r?.members ?? r?.data ?? r ?? []); setBusy(false); })
      // MEDIUM-14: surface load failure instead of showing empty "0 members"
      .catch(e => { setWsErr(e?.message ?? 'Failed to load workspace members'); setBusy(false); });
  }, []);

  useEffect(() => {
    if (!preselectedWsId || wss.length === 0 || selected) return;
    const match = wss.find(w => w.id === preselectedWsId);
    if (match) loadWsMembers(match);
  }, [preselectedWsId, wss, selected, loadWsMembers]);

  const loadOrgMembers = useCallback(() => {
    if (allMembers.length > 0) return;
    membersApi.list(orgId, { pageSize: 200 })
      .then(r => setAllMembers(r?.members ?? r?.data ?? r ?? []))
      .catch(() => {});
  }, [orgId, allMembers.length]);

  const addMember = useCallback(async () => {
    if (!selected || !addMemberId) return;
    setActionBusy(b => ({ ...b, add: true }));
    try {
      const res = await wsApi.addMember(selected.id, { memberId: addMemberId, wsRole: addRole });
      const member = allMembers.find(m => m.id === addMemberId);
      setWsMembers(prev => [...prev, { id: res.id, memberId: addMemberId, wsRole: addRole, member: member ?? { id: addMemberId } }]);
      toast({ title: 'Member added to workspace', tone: 'success' });
      setShowAdd(false);
      setAddMemberId('');
    } catch (e) {
      toast({ title: e?.message || 'Failed to add member', tone: 'error' });
    } finally {
      setActionBusy(b => ({ ...b, add: false }));
    }
  }, [selected, addMemberId, addRole, allMembers, toast]);

  const removeMember = useCallback(async (wm) => {
    if (!selected) return;
    const targetIsAdmin  = wm.wsRole === 'WORKSPACE_ADMIN';
    const wsAdminCount   = wsMembers.filter(m => m.wsRole === 'WORKSPACE_ADMIN').length;
    if (!isOrgAdmin && targetIsAdmin) {
      toast({ title: 'Workspace Admins cannot remove a peer Workspace Admin', tone: 'error' });
      return;
    }
    if (targetIsAdmin && wsAdminCount === 1) {
      toast({ title: 'Cannot remove the last Workspace Admin from this workspace', tone: 'error' });
      return;
    }
    setActionBusy(b => ({ ...b, [wm.id]: true }));
    try {
      await wsApi.removeMember(selected.id, wm.id);
      setWsMembers(prev => prev.filter(x => x.id !== wm.id));
      toast({ title: 'Member removed from workspace', tone: 'success' });
    } catch (e) {
      toast({ title: e?.message || 'Failed to remove member', tone: 'error' });
    } finally {
      setActionBusy(b => ({ ...b, [wm.id]: false }));
    }
  }, [selected, wsMembers, isOrgAdmin, toast]);

  const changeRole = useCallback(async (wm, newRole) => {
    if (!selected) return;
    const targetIsAdmin  = wm.wsRole === 'WORKSPACE_ADMIN';
    const wsAdminCount   = wsMembers.filter(m => m.wsRole === 'WORKSPACE_ADMIN').length;
    if (!isOrgAdmin && targetIsAdmin) {
      toast({ title: 'Workspace Admins cannot modify a peer Workspace Admin', tone: 'error' });
      return;
    }
    if (targetIsAdmin && newRole !== 'WORKSPACE_ADMIN' && wsAdminCount === 1) {
      toast({ title: 'Cannot demote the last Workspace Admin', tone: 'error' });
      return;
    }
    setActionBusy(b => ({ ...b, [wm.id]: true }));
    try {
      await wsApi.updateMember(selected.id, wm.id, { wsRole: newRole });
      setWsMembers(prev => prev.map(x => x.id === wm.id ? { ...x, wsRole: newRole } : x));
      toast({ title: 'Role updated', tone: 'success' });
    } catch (e) {
      toast({ title: e?.message || 'Failed to update role', tone: 'error' });
    } finally {
      setActionBusy(b => ({ ...b, [wm.id]: false }));
    }
  }, [selected, wsMembers, isOrgAdmin, toast]);

  const assignedMemberIds = new Set(wsMembers.map(m => m.memberId || m.member?.id));
  const availableMembers  = allMembers.filter(m => !assignedMemberIds.has(m.id));
  // LOW-13: filter workspace list by search query
  const filteredWss = wsSearch.trim()
    ? wss.filter(w => w.name?.toLowerCase().includes(wsSearch.toLowerCase()) || w.slug?.toLowerCase().includes(wsSearch.toLowerCase()))
    : wss;

  return (
    <div style={{ display: 'flex', flexDirection: 'column' }}>
      {/* M-06: CTA when no workspaces exist */}
      {wss.length === 0 && (
        <div className="nx-iam-banner nx-iam-banner--warn" style={{ margin: '12px 16px 0' }}>
          <NexusIcon name="AlertTriangle" size={13} style={{ color: '#b45309', flexShrink: 0, marginTop: 1 }} />
          <span>No workspaces exist for this organization yet. Create a workspace in the Workspaces module before managing workspace access here.</span>
        </div>
      )}
    <div className="nx-iam-split">
      {/* Left: workspace list */}
      <div className="nx-iam-split-left">
        {/* LOW-13: search input for workspace list */}
        <div className="nx-iam-search-bar">
          <NexusIcon name="Search" size={13} />
          <input
            className="nx-input--inline"
            placeholder="Search workspaces..."
            value={wsSearch}
            onChange={e => setWsSearch(e.target.value)}
          />
        </div>
        <div className="nx-iam-list-head">Workspaces ({filteredWss.length}{wsSearch ? ` of ${wss.length}` : ''})</div>
        <div className="nx-iam-list">
          {filteredWss.length === 0 && <div className="nx-empty-sm">{wsSearch ? 'No matching workspaces' : 'No workspaces found'}</div>}
          {filteredWss.map(ws => (
            <button key={ws.id} className={'nx-iam-member-row' + (selected?.id === ws.id ? ' is-selected' : '')} onClick={() => loadWsMembers(ws)}>
              <div className="nx-iam-avatar nx-iam-avatar--ws"><NexusIcon name="LayoutGrid" size={13} /></div>
              <div className="nx-iam-member-info">
                <span className="nx-iam-member-name">{ws.name}</span>
                <span className="nx-iam-member-email">{ws.slug || ws.status}</span>
              </div>
              <Pill tone={STATUS_TONE[ws.status] ?? 'slate'} style={{ fontSize: 10 }}>{ws.status}</Pill>
            </button>
          ))}
        </div>
      </div>

      {/* Right: workspace members */}
      <div className="nx-iam-split-right">
        {!selected && (
          <div className="nx-iam-detail-empty">
            <NexusIcon name="Server" size={28} />
            <span>Select a workspace to manage its members</span>
          </div>
        )}
        {selected && (
          <>
            <div className="nx-iam-detail-head">
              <div>
                <div className="nx-iam-detail-name">{selected.name}</div>
                <div className="nx-iam-detail-meta">
                  <span className="nx-iam-detail-sub">{selected.slug}</span>
                  <Pill tone={STATUS_TONE[selected.status] ?? 'slate'} style={{ fontSize: 10 }}>{selected.status}</Pill>
                  {!busy && <Pill tone="slate" style={{ fontSize: 10 }}>{wsMembers.length} member{wsMembers.length !== 1 ? 's' : ''}</Pill>}
                </div>
              </div>
              {canManage && (
                <Btn size="sm" variant="secondary" onClick={() => { loadOrgMembers(); setShowAdd(true); }}>
                  <NexusIcon name="Plus" size={13} /> Add Member
                </Btn>
              )}
            </div>
            {busy && <div className="nx-loading"><div className="nx-spinner" /></div>}
            {!busy && wsErr && (
              <div className="nx-empty" style={{ paddingTop: 40 }}><NexusIcon name="AlertCircle" size={18} /><span>{wsErr}</span></div>
            )}
            {!busy && !wsErr && (
              <>
                <div className="nx-iam-ws-count">{wsMembers.length} member{wsMembers.length !== 1 ? 's' : ''}</div>
                {wsMembers.length === 0 && <div className="nx-empty-sm">No members in this workspace</div>}
                {wsMembers.map(wm => {
                  const name  = wm.member?.name || wm.member?.user?.name || wm.name || 'Unknown';
                  const email = wm.member?.email || wm.member?.user?.email || wm.email || '';
                  const role  = wm.wsRole || 'WORKSPACE_USER';
                  const isBusy = actionBusy[wm.id];
                  const canEditRow = canManage && (isOrgAdmin || role !== 'WORKSPACE_ADMIN');
                  return (
                    <div key={wm.id} className="nx-iam-ws-row">
                      <div className="nx-iam-member-avatar" style={{ width: 28, height: 28, fontSize: 11 }}>{name[0]?.toUpperCase() ?? '?'}</div>
                      <div className="nx-iam-ws-name">
                        <span>{name}</span>
                        <span className="nx-iam-member-email">{email}</span>
                      </div>
                      {canEditRow
                        ? <select className="nx-input" style={{ fontSize: 12, height: 28, width: 140 }} value={role} disabled={isBusy} onChange={e => changeRole(wm, e.target.value)}>
                            <option value="WORKSPACE_USER">Workspace User</option>
                            <option value="WORKSPACE_ADMIN">Workspace Admin</option>
                          </select>
                        : <Pill tone={WS_ROLE_TONE[role] ?? 'slate'}>{WS_ROLE_LABEL[role] ?? role}</Pill>}
                      {canEditRow && (
                        <button className="nx-icon-btn nx-icon-btn--danger" disabled={isBusy} onClick={() => removeMember(wm)} title="Remove member">
                          <NexusIcon name="Trash2" size={13} />
                        </button>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </>
        )}
      </div>

      {showAdd && (
        <div className="nx-modal-overlay" onClick={() => setShowAdd(false)}>
          <div className="nx-modal" style={{ width: 420 }} onClick={e => e.stopPropagation()}>
            <div className="nx-modal-head">
              <span>Add Member to {selected?.name}</span>
              <button className="nx-modal-close" onClick={() => setShowAdd(false)}><NexusIcon name="X" size={14} /></button>
            </div>
            <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 12 }}>
              <label className="nx-label">Member</label>
              <MemberCombobox members={availableMembers} value={addMemberId} onChange={setAddMemberId} />
              <label className="nx-label">Role</label>
              <select className="nx-input" value={addRole} onChange={e => setAddRole(e.target.value)}>
                <option value="WORKSPACE_USER">Workspace User</option>
                <option value="WORKSPACE_ADMIN">Workspace Admin</option>
              </select>
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
                <Btn size="sm" variant="ghost" onClick={() => setShowAdd(false)}>Cancel</Btn>
                <Btn size="sm" variant="primary" disabled={!addMemberId || actionBusy.add} onClick={addMember}>Add</Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
    </div>
  );
}

/* ── Tab: Assignments (landing — By Member / By Workspace + Effective Access drawer) ── */
function AssignmentsTab({ orgId, wss, preselectedMemberId, preselectedWsId }) {
  const [perspective,    setPerspective]    = useState(
    preselectedWsId && !preselectedMemberId ? 'workspace' : 'member'
  );
  const [drawerMemberId, setDrawerMemberId] = useState(null);

  return (
    <div className="nx-iam-section">
      <div className="nx-iam-toolbar">
        <div className="nx-iam-toolbar-left">
          <h3 className="nx-iam-section-title">Assignments</h3>
          <button className={`nx-filter-pill${perspective === 'member' ? ' is-active' : ''}`}
            onClick={() => setPerspective('member')}>By Member</button>
          <button className={`nx-filter-pill${perspective === 'workspace' ? ' is-active' : ''}`}
            onClick={() => setPerspective('workspace')}>By Workspace</button>
        </div>
      </div>

      {perspective === 'member'
        ? <MembersAccessPanel orgId={orgId} wss={wss} preselectedMemberId={preselectedMemberId} onCheckAccess={setDrawerMemberId} />
        : <WorkspaceAccessPanel orgId={orgId} wss={wss} preselectedWsId={preselectedWsId} />
      }

      {drawerMemberId && (
        <EffectiveAccessDrawer orgId={orgId} memberId={drawerMemberId} onClose={() => setDrawerMemberId(null)} />
      )}
    </div>
  );
}



/* ── L-05: Searchable combobox (replaces native <select> for member picker) ── */
function MemberCombobox({ members, value, onChange }) {
  const [query,  setQuery]  = useState('');
  const [open,   setOpen]   = useState(false);
  const wrapRef = useRef(null);

  useEffect(() => {
    const onDown = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const filtered = useMemo(() => {
    if (!query) return members;
    const q = query.toLowerCase();
    return members.filter(m =>
      (m.name || '').toLowerCase().includes(q) || (m.email || '').toLowerCase().includes(q),
    );
  }, [members, query]);

  const selectedMember = members.find(m => m.id === value);

  const handleSelect = (id) => { onChange(id); setOpen(false); setQuery(''); };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: 300 }}>
      <button
        type="button"
        className="nx-input"
        style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', padding: '0 10px', height: 34, justifyContent: 'space-between', width: '100%', textAlign: 'left' }}
        onClick={() => setOpen(o => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, color: selectedMember ? 'var(--nx-text)' : 'var(--nx-text-muted)', fontSize: 12 }}>
          {selectedMember ? (selectedMember.name || selectedMember.email) : '— select a member —'}
        </span>
        <NexusIcon name={open ? 'ChevronUp' : 'ChevronDown'} size={12} style={{ flexShrink: 0, marginLeft: 6 }} />
      </button>
      {open && (
        <div role="listbox" className="nx-combobox-dropdown">
          <div className="nx-combobox-search">
            <input
              className="nx-input"
              autoFocus
              placeholder="Search members..."
              value={query}
              onChange={e => setQuery(e.target.value)}
              style={{ width: '100%', height: 28, fontSize: 12 }}
            />
          </div>
          <div className="nx-combobox-list">
            {filtered.length === 0 && <div className="nx-empty-sm">No matching members</div>}
            {filtered.map(m => (
              <button
                key={m.id}
                role="option"
                aria-selected={value === m.id}
                className="nx-iam-ws-pick-row"
                style={{ gap: 8, padding: '7px 12px' }}
                onClick={() => handleSelect(m.id)}
              >
                <div className="nx-iam-avatar" style={{ width: 24, height: 24, fontSize: 11 }}>
                  {(m.name || m.email || '?')[0].toUpperCase()}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--nx-text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{m.name || m.email}</div>
                  {m.name && m.email && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{m.email}</div>}
                </div>
                {value === m.id && <NexusIcon name="Check" size={12} style={{ flexShrink: 0, color: 'var(--nx-accent)' }} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* CAP_GROUP_LABELS imported from capabilities-meta.js */

function EffectiveAccessDrawer({ orgId, memberId, onClose }) {
  const [access,    setAccess]    = useState(null);
  const [busy,      setBusy]      = useState(false);
  const [err,       setErr]       = useState(null);
  const [capSearch, setCapSearch] = useState('');

  useEffect(() => {
    if (!memberId) return;
    setAccess(null); setErr(null); setBusy(true);
    orgRolesApi.getEffectiveAccess(orgId, memberId)
      .then(r => { setAccess(r); setBusy(false); })
      .catch(e => { setErr(e?.message || 'Failed to load effective access'); setBusy(false); });
  }, [orgId, memberId]);

  const member         = access?.member;
  const orgPermissions = access?.orgPermissions;
  const wsAssignments  = access?.workspaceAssignments ?? [];
  const orgCaps        = access?.orgPermissions?.capabilities ?? [];
  const wsCaps         = access?.permissionSource?.workspace ?? [];
  const finalCaps      = access?.permissionSource?.final ?? [];
  const customRole     = member?.customRole ?? orgPermissions?.customRole;

  const filteredFinalCaps = useMemo(() => {
    if (!capSearch.trim()) return finalCaps;
    const q = capSearch.toLowerCase();
    return finalCaps.filter(c => c.toLowerCase().includes(q) || (CAP_LABELS[c] ?? '').toLowerCase().includes(q));
  }, [finalCaps, capSearch]);

  const finalCapGroups = useMemo(() => {
    const groups = {};
    filteredFinalCaps.forEach(c => {
      const prefix = c.split('.')[0];
      if (!groups[prefix]) groups[prefix] = [];
      groups[prefix].push(c);
    });
    return groups;
  }, [filteredFinalCaps]);

  const memberName = member?.name || member?.user?.name || member?.email || 'Member';

  return (
    <div className="nx-iam-drawer-overlay" onClick={onClose}>
      <div className="nx-iam-drawer" onClick={e => e.stopPropagation()}>
        <div className="nx-iam-drawer-head">
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: 'var(--nx-text)' }}>Effective Access</div>
            <div style={{ fontSize: 12, color: 'var(--nx-text-muted)' }}>{memberName}</div>
          </div>
          {access && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <Pill tone="green" style={{ fontWeight: 700 }}>{finalCaps.length} caps</Pill>
              <Pill tone={ROLE_TONE[member?.orgRole] ?? 'slate'}>{member?.orgRole ?? 'MEMBER'}</Pill>
            </div>
          )}
          <button className="nx-modal-close" onClick={onClose} style={{ marginLeft: 8 }}>
            <NexusIcon name="X" size={14} />
          </button>
        </div>
        <div className="nx-iam-drawer-body">
          {busy && <div className="nx-loading"><div className="nx-spinner" /></div>}
          {err  && <div className="nx-empty" style={{ padding: 24 }}><NexusIcon name="AlertCircle" size={18} /><span>{err}</span></div>}
          {access && !busy && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {customRole && (
                <div className="nx-card">
                  <div className="nx-card-head"><NexusIcon name="Shield" size={13} /><span>Custom Role Override</span></div>
                  <div className="nx-iam-row">
                    <span className="nx-iam-row-name">{customRole.name}</span>
                    {customRole.description && <span style={{ color: 'var(--nx-text-muted)', fontSize: 12 }}>{customRole.description}</span>}
                    <Pill tone="purple" style={{ marginLeft: 'auto' }}>{(customRole.permissions ?? []).length} extra perms</Pill>
                  </div>
                </div>
              )}
              <div className="nx-card">
                <div className="nx-card-head"><NexusIcon name="GitMerge" size={13} /><span>Permission Sources</span></div>
                <div className="nx-iam-row">
                  <span className="nx-iam-row-label">Org role</span>
                  <Pill tone={ROLE_TONE[member?.orgRole] ?? 'slate'}>{orgPermissions?.systemRole ?? member?.orgRole ?? '—'}</Pill>
                </div>
                <div className="nx-iam-row"><span className="nx-iam-row-label">Org caps</span><Pill tone="blue">{orgCaps.length}</Pill></div>
                <div className="nx-iam-row"><span className="nx-iam-row-label">WS caps</span><Pill tone="slate">{wsCaps.length}</Pill></div>
                <div className="nx-iam-row">
                  <span className="nx-iam-row-label">Final total</span>
                  <Pill tone="green" style={{ fontWeight: 700 }}>{finalCaps.length}</Pill>
                </div>
              </div>
              {wsAssignments.length > 0 && (
                <div className="nx-card">
                  <div className="nx-card-head">
                    <NexusIcon name="LayoutGrid" size={13} />
                    <span>Workspace Assignments</span>
                    <Pill tone="slate" style={{ marginLeft: 'auto' }}>{wsAssignments.length}</Pill>
                  </div>
                  {wsAssignments.map((a, i) => (
                    <div key={a.workspace?.id ?? i} className="nx-iam-ws-row" style={{ padding: '10px 16px', borderBottom: '1px solid color-mix(in srgb, var(--nx-border) 20%, transparent)' }}>
                      <NexusIcon name="Server" size={13} style={{ color: 'var(--nx-text-muted)', flexShrink: 0 }} />
                      <div className="nx-iam-ws-name">
                        <span>{a.workspace?.name ?? '—'}</span>
                        {a.workspace?.status && <span style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>{a.workspace.status}</span>}
                      </div>
                      <Pill tone={WS_ROLE_TONE[a.wsRole] ?? 'slate'}>{WS_ROLE_LABEL[a.wsRole] ?? a.wsRole}</Pill>
                      <Pill tone="slate" style={{ fontSize: 10, marginLeft: 4 }}>{(a.capabilities ?? []).length} caps</Pill>
                    </div>
                  ))}
                </div>
              )}
              <div className="nx-card">
                <div className="nx-card-head">
                  <NexusIcon name="Key" size={13} />
                  <span>Effective Capabilities</span>
                  <Pill tone="green" style={{ marginLeft: 'auto', fontWeight: 700 }}>{finalCaps.length}</Pill>
                </div>
                {finalCaps.length > 0 && (
                  <div style={{ padding: '8px 16px', borderBottom: '1px solid var(--nx-border)' }}>
                    <div className="nx-iam-search-box" style={{ width: '100%' }}>
                      <NexusIcon name="Search" size={12} />
                      <input className="nx-input--inline" placeholder="Filter by name or key..." value={capSearch} onChange={e => setCapSearch(e.target.value)} />
                      {capSearch && <span style={{ fontSize: 11, color: 'var(--nx-text-muted)', flexShrink: 0 }}>{filteredFinalCaps.length}/{finalCaps.length}</span>}
                    </div>
                  </div>
                )}
                {filteredFinalCaps.length === 0
                  ? <div className="nx-empty-sm">{capSearch ? 'No matching capabilities' : 'No capabilities assigned'}</div>
                  : Object.entries(finalCapGroups).map(([prefix, list]) => (
                    <div key={prefix} style={{ padding: '8px 16px 10px', borderBottom: '1px solid color-mix(in srgb, var(--nx-border) 32%, transparent)' }}>
                      <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--nx-text-muted)', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                        {CAP_GROUP_LABELS[prefix] ?? prefix} <span style={{ color: 'var(--nx-text-faint)', fontWeight: 400 }}>({list.length})</span>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                        {list.map(cap => (
                          <span key={cap} className="nx-iam-cap-badge nx-iam-cap-badge--on" title={cap}>{CAP_LABELS[cap] ?? cap}</span>
                        ))}
                      </div>
                    </div>
                  ))
                }
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


/* ── Tab: Security ───────────────────────────────────────────────────── */
// Backend audit Phase 7A — all 13 policy fields are Category B:
// stored in DB (mfaRequired/ssoEnabled in Organization; remaining 11 in
// OrgSettings.policies JSON), returned with _enforced:false, no runtime effect.
// Category A features (lockout, token rotation, rate limiting, RBAC) run in
// backend infrastructure — no configurable UI surface. IP allowlist is
// Category A-partial (4/~25 org controllers guarded). Full security operations
// (IP allowlist CRUD, active sessions, session revocation) live in OrgSecurityCenter.

// Identity Security section is handled by IdentitySecuritySection component below.
// Access Controls and Workspace Policies use the generic policy renderer.
const POLICY_SECTIONS = [
  {
    id: 'access',
    title: 'Access Controls',
    icon: 'UserCheck',
    policies: [
      { key: 'allowExternalMembers', label: 'Allow External Members',   type: 'bool',   desc: 'Permit members from outside the organization domain to be invited.' },
      { key: 'allowCustomRoles',     label: 'Allow Custom Roles',       type: 'bool',   desc: 'Enable creation and assignment of custom IAM roles within the organization.' },
      { key: 'autoProvision',        label: 'Auto Provision',           type: 'bool',   desc: 'Automatically provision members from SSO on first login.' },
      { key: 'accessExpiryDays',     label: 'Access Expiry (days)',     type: 'number', min: 1, max: 3650, desc: 'Days before workspace access expires and requires renewal (leave blank to disable).' },
    ],
  },
  {
    id: 'workspace',
    title: 'Workspace Policies',
    icon: 'LayoutGrid',
    policies: [
      { key: 'requireApprovalForNewWorkspace', label: 'Workspace Creation Approval',   type: 'bool',   desc: 'New workspaces require admin approval before activation.' },
      { key: 'workspaceAssignmentApproval',    label: 'Workspace Assignment Approval', type: 'bool',   desc: 'Workspace member assignments require admin approval.' },
      { key: 'temporaryWorkspaceAccess',       label: 'Allow Temporary Access',        type: 'bool',   desc: 'Members may be granted time-limited workspace access.' },
      { key: 'maxWorkspaceAdmins',             label: 'Max Workspace Admins',          type: 'number', min: 1, max: 100,   desc: 'Maximum number of admins per workspace (leave blank for unlimited).' },
      { key: 'maxMembersPerWorkspace',         label: 'Max Members / Workspace',       type: 'number', min: 1, max: 10000, desc: 'Maximum members per workspace (leave blank for unlimited).' },
    ],
  },
];

// Category A items — enforced at runtime in backend infrastructure; no UI controls.
// Surfaced here as read-only posture indicators so admins can verify enforcement status.
const POSTURE_A = [
  { id: 'lockout',  label: 'Account Lockout',       icon: 'ShieldAlert', partial: false, desc: '5 failed attempts → 15-minute lockout. Enforced inline in the login flow.' },
  { id: 'rotation', label: 'Token Rotation',         icon: 'RefreshCw',   partial: false, desc: 'Refresh tokens are SHA-256 hashed and rotated atomically on every use.' },
  { id: 'rate',     label: 'Rate Limiting',          icon: 'Zap',         partial: false, desc: 'Login: 60 req/min · Global: 200 req/min · Register: 5/min.' },
  { id: 'rbac',     label: 'RBAC Enforcement',       icon: 'Shield',      partial: false, desc: 'Role-based capability guard applied globally to all API routes.' },
  { id: 'ip',       label: 'IP Allowlist',           icon: 'MapPin',      partial: false, desc: 'Network access control rules applied to authenticated API requests. Configure allowed IP ranges in Security Center.' },
];

const NotEnforcedBadge = () => (
  <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
    background: 'rgba(100,116,139,0.1)', color: 'var(--nx-text-muted)', border: '1px solid rgba(100,116,139,0.2)', whiteSpace: 'nowrap', flexShrink: 0 }}>
    Saved
  </span>
);

const EnforcedBadge = ({ partial }) => (
  <span style={{ fontSize: 10, fontWeight: 600, padding: '1px 6px', borderRadius: 4,
    background: partial ? 'rgba(245,158,11,0.1)' : 'rgba(16,185,129,0.1)',
    color: partial ? '#d97706' : '#059669',
    border: `1px solid ${partial ? 'rgba(245,158,11,0.25)' : 'rgba(16,185,129,0.25)'}`, whiteSpace: 'nowrap', flexShrink: 0 }}>
    {partial ? '⚠ Partial' : '✓ Enforced'}
  </span>
);

/* ── SSO Provider Drawer ─────────────────────────────────────────────────── */

const BLANK_SSO_FORM = {
  name: '', protocol: 'SAML',
  issuer: '', ssoUrl: '', sloUrl: '', certificate: '',
  clientId: '', clientSecret: '', discoveryUrl: '',
  isEnabled: false,
};

function providerToForm(p) {
  return {
    name: p.name ?? '', protocol: p.protocol ?? 'SAML',
    issuer: p.issuer ?? '', ssoUrl: p.ssoUrl ?? '', sloUrl: p.sloUrl ?? '',
    certificate: p.certificate ?? '', clientId: p.clientId ?? '',
    clientSecret: '',  // write-only — never pre-fill
    discoveryUrl: p.discoveryUrl ?? '', isEnabled: p.isEnabled ?? false,
  };
}

function SsoProviderListItem({ provider: p, onEdit, onSetDefault, onDelete, deletingId }) {
  return (
    <div className="nx-card" style={{ padding: '12px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <NexusIcon name={p.protocol === 'SAML' ? 'ShieldCheck' : 'Globe'} size={14} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
        <span style={{ fontWeight: 500, fontSize: 13, flex: 1 }}>{p.name}</span>
        <Pill tone={p.protocol === 'SAML' ? 'blue' : 'purple'}>{p.protocol}</Pill>
        <Pill tone={p.isEnabled ? 'green' : 'gray'}>{p.isEnabled ? 'Enabled' : 'Disabled'}</Pill>
        {p.isDefault && <Pill tone="amber">Default</Pill>}
      </div>
      {p.issuer && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 6 }}>Issuer: {p.issuer}</div>}
      {p.discoveryUrl && <div style={{ fontSize: 11, color: 'var(--nx-text-muted)', marginTop: 6 }}>Discovery: {p.discoveryUrl}</div>}
      <div style={{ display: 'flex', gap: 6, marginTop: 10 }}>
        <Btn size="sm" variant="ghost" onClick={() => onEdit(p)}>Edit</Btn>
        {!p.isDefault && <Btn size="sm" variant="ghost" onClick={() => onSetDefault(p.id)}>Set Default</Btn>}
        <Btn
          size="sm" variant="ghost" disabled={deletingId === p.id}
          onClick={() => onDelete(p.id)}
          style={{ marginLeft: 'auto', color: 'var(--nx-danger, #dc2626)' }}
        >
          {deletingId === p.id ? 'Deleting…' : 'Delete'}
        </Btn>
      </div>
    </div>
  );
}

function SsoProviderFormBody({ form, ff, editing }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="nx-form-row">
        <span className="nx-label">Protocol</span>
        {editing ? (
          <Pill tone={form.protocol === 'SAML' ? 'blue' : 'purple'}>{form.protocol}</Pill>
        ) : (
          <div style={{ display: 'flex', gap: 8 }}>
            {['SAML', 'OIDC'].map(proto => (
              <button key={proto} type="button"
                className={`nx-tab-pill${form.protocol === proto ? ' nx-tab-pill--active' : ''}`}
                onClick={() => ff('protocol', proto)}
              >{proto}</button>
            ))}
          </div>
        )}
      </div>
      <div className="nx-form-row">
        <label className="nx-label" htmlFor="sso-name">Name</label>
        <input id="sso-name" className="nx-input" placeholder="e.g. Okta SAML" value={form.name} onChange={e => ff('name', e.target.value)} />
      </div>
      {form.protocol === 'SAML' && (
        <>
          <div className="nx-form-row">
            <label className="nx-label" htmlFor="sso-issuer">Issuer / Entity ID</label>
            <input id="sso-issuer" className="nx-input" placeholder="https://idp.example.com/issuer" value={form.issuer} onChange={e => ff('issuer', e.target.value)} />
          </div>
          <div className="nx-form-row">
            <label className="nx-label" htmlFor="sso-ssoUrl">SSO URL</label>
            <input id="sso-ssoUrl" className="nx-input" placeholder="https://idp.example.com/sso/saml" value={form.ssoUrl} onChange={e => ff('ssoUrl', e.target.value)} />
          </div>
          <div className="nx-form-row">
            <label className="nx-label" htmlFor="sso-sloUrl">SLO URL (optional)</label>
            <input id="sso-sloUrl" className="nx-input" placeholder="https://idp.example.com/slo/saml" value={form.sloUrl} onChange={e => ff('sloUrl', e.target.value)} />
          </div>
          <div className="nx-form-row">
            <label className="nx-label" htmlFor="sso-cert">Signing Certificate (PEM)</label>
            <textarea id="sso-cert" className="nx-input" rows={5}
              placeholder={'-----BEGIN CERTIFICATE-----\n…\n-----END CERTIFICATE-----'}
              value={form.certificate} onChange={e => ff('certificate', e.target.value)}
              style={{ fontFamily: 'monospace', fontSize: 11, resize: 'vertical' }}
            />
          </div>
        </>
      )}
      {form.protocol === 'OIDC' && (
        <>
          <div className="nx-form-row">
            <label className="nx-label" htmlFor="sso-disco">Discovery URL</label>
            <input id="sso-disco" className="nx-input" placeholder="https://accounts.example.com/.well-known/openid-configuration" value={form.discoveryUrl} onChange={e => ff('discoveryUrl', e.target.value)} />
          </div>
          <div className="nx-form-row">
            <label className="nx-label" htmlFor="sso-issuer-oidc">Issuer (if no discovery URL)</label>
            <input id="sso-issuer-oidc" className="nx-input" placeholder="https://accounts.example.com" value={form.issuer} onChange={e => ff('issuer', e.target.value)} />
          </div>
          <div className="nx-form-row">
            <label className="nx-label" htmlFor="sso-clientId">Client ID</label>
            <input id="sso-clientId" className="nx-input" placeholder="Client ID from your IdP" value={form.clientId} onChange={e => ff('clientId', e.target.value)} />
          </div>
          <div className="nx-form-row">
            <label className="nx-label" htmlFor="sso-secret">
              Client Secret
              {editing && <span style={{ fontSize: 10, color: 'var(--nx-text-muted)', marginLeft: 6 }}>(leave blank to keep existing)</span>}
            </label>
            <input id="sso-secret" className="nx-input" type="password"
              placeholder={editing ? '••••••••' : 'Client secret'}
              value={form.clientSecret} onChange={e => ff('clientSecret', e.target.value)}
              autoComplete="new-password"
            />
          </div>
        </>
      )}
      <div className="nx-form-row" style={{ alignItems: 'center' }}>
        <span className="nx-label">Enable provider</span>
        <label className="nx-toggle">
          <input type="checkbox" checked={form.isEnabled} onChange={e => ff('isEnabled', e.target.checked)} />
          <span className="nx-toggle-slider" />
        </label>
      </div>
    </div>
  );
}

function SsoProviderDrawer({ orgId, providers, onClose, onProvidersChange }) {
  const toast = useToast();
  const [mode, setMode] = useState('list');
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(BLANK_SSO_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const ff = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const openCreate = () => { setEditing(null); setForm(BLANK_SSO_FORM); setMode('form'); };
  const openEdit   = (p) => { setEditing(p);  setForm(providerToForm(p)); setMode('form'); };
  const backToList = () => { setMode('list'); setEditing(null); };

  const handleSubmit = async () => {
    if (!form.name.trim()) { toast({ tone: 'error', title: 'Provider name is required' }); return; }
    setSubmitting(true);
    try {
      const payload = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ''));
      if (editing) {
        const result = await orgSsoApi.update(orgId, editing.id, payload);
        onProvidersChange(providers.map(p => p.id === editing.id ? result : p));
        toast({ tone: 'success', title: 'Provider updated' });
      } else {
        const result = await orgSsoApi.create(orgId, payload);
        onProvidersChange([...providers, result]);
        toast({ tone: 'success', title: 'Provider created' });
      }
      backToList();
    } catch (e) {
      toast({ tone: 'error', title: e?.message || 'Failed to save provider' });
    } finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await orgSsoApi.remove(orgId, id);
      onProvidersChange(providers.filter(p => p.id !== id));
      toast({ tone: 'success', title: 'Provider deleted' });
    } catch (e) {
      toast({ tone: 'error', title: e?.message || 'Failed to delete provider' });
    } finally { setDeletingId(null); }
  };

  const handleSetDefault = async (id) => {
    try {
      await orgSsoApi.setDefault(orgId, id);
      onProvidersChange(providers.map(p => ({ ...p, isDefault: p.id === id })));
      toast({ tone: 'success', title: 'Default provider updated' });
    } catch (e) {
      toast({ tone: 'error', title: e?.message || 'Failed to set default provider' });
    }
  };

  const drawerTitle = mode === 'form'
    ? (editing ? `Edit: ${editing.name}` : 'Add Identity Provider')
    : 'Identity Providers';

  return (
    <>
      <div aria-hidden="true" onClick={onClose}
        style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 200 }}
      />
      <div role="dialog" aria-modal="true" aria-label={drawerTitle}
        style={{ position: 'fixed', top: 0, right: 0, bottom: 0, width: 520,
          background: 'var(--nx-surface)', borderLeft: '1px solid var(--nx-border)',
          zIndex: 201, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '16px 20px',
          borderBottom: '1px solid var(--nx-border)', flexShrink: 0 }}>
          {mode === 'form' && (
            <button className="nx-icon-btn" aria-label="Back" onClick={backToList} style={{ marginRight: 4 }}>
              <NexusIcon name="ArrowLeft" size={16} />
            </button>
          )}
          <NexusIcon name="KeySquare" size={16} style={{ color: 'var(--nx-accent)' }} />
          <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--nx-text)' }}>{drawerTitle}</span>
          <button className="nx-icon-btn" aria-label="Close" onClick={onClose} style={{ marginLeft: 'auto' }}>
            <NexusIcon name="X" size={16} />
          </button>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 20px' }}>
          {mode === 'list' ? (
            providers.length === 0 ? (
              <div className="nx-empty" style={{ padding: '48px 0' }}>
                <NexusIcon name="KeyRound" size={20} />
                <span>No identity providers configured. Add a SAML or OIDC provider to enable SSO.</span>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {providers.map(p => (
                  <SsoProviderListItem
                    key={p.id} provider={p}
                    onEdit={openEdit} onSetDefault={handleSetDefault}
                    onDelete={handleDelete} deletingId={deletingId}
                  />
                ))}
              </div>
            )
          ) : (
            <SsoProviderFormBody form={form} ff={ff} editing={editing} />
          )}
        </div>
        <div style={{ borderTop: '1px solid var(--nx-border)', padding: '12px 20px',
          display: 'flex', gap: 8, flexShrink: 0 }}>
          {mode === 'list' ? (
            <>
              <Btn variant="primary" size="sm" onClick={openCreate}>
                <NexusIcon name="Plus" size={13} /> Add Provider
              </Btn>
              <Btn variant="ghost" size="sm" onClick={onClose} style={{ marginLeft: 'auto' }}>Close</Btn>
            </>
          ) : (
            <>
              <Btn variant="primary" size="sm" disabled={submitting} onClick={handleSubmit}>
                {submitting ? 'Saving…' : (editing ? 'Save Changes' : 'Add Provider')}
              </Btn>
              <Btn variant="ghost" size="sm" onClick={backToList}>Cancel</Btn>
            </>
          )}
        </div>
      </div>
    </>
  );
}

/* ── Identity Security + Password Complexity section ─────────────────────── */

const PW_COMPLEXITY_RULES = [
  { key: 'passwordRequireUppercase', label: 'Require Uppercase', desc: 'Password must include at least one uppercase letter (A–Z).' },
  { key: 'passwordRequireLowercase', label: 'Require Lowercase', desc: 'Password must include at least one lowercase letter (a–z).' },
  { key: 'passwordRequireNumbers',   label: 'Require Numbers',   desc: 'Password must include at least one digit (0–9).' },
  { key: 'passwordRequireSymbols',   label: 'Require Symbols',   desc: 'Password must include at least one special character (!@#$%…).' },
];

function IdentitySecuritySection({ draft, canEdit, saving, setField, orgId }) {
  const [ssoProviders, setSsoProviders] = useState([]);
  const [showSsoDrawer, setShowSsoDrawer] = useState(false);

  const loadProviders = useCallback(async () => {
    try {
      const data = await orgSsoApi.list(orgId);
      setSsoProviders(Array.isArray(data) ? data : (data?.providers ?? []));
    } catch { /* non-critical — providers list shows empty on failure */ }
  }, [orgId]);

  useEffect(() => { loadProviders(); }, [loadProviders]);

  const hasProviders = ssoProviders.length > 0;
  const providerCountLabel = ssoProviders.length === 0
    ? 'Configure Identity Provider'
    : `${ssoProviders.length} provider${ssoProviders.length === 1 ? '' : 's'} configured`;

  return (
    <>
      <div className="nx-card">
        <div className="nx-card-head">
          <NexusIcon name="Lock" size={14} />
          <span>Identity Security</span>
        </div>
        <div style={{ padding: '4px 0' }}>
          {/* MFA */}
          <div className="nx-iam-policy-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="nx-iam-row-name">Require MFA</span>
                <NotEnforcedBadge />
              </div>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                All members must enroll in multi-factor authentication before accessing the organization.
              </div>
            </div>
            <label className="nx-toggle">
              <input type="checkbox" aria-label="Require MFA"
                checked={!!draft?.mfaRequired} disabled={!canEdit || saving}
                onChange={e => setField('mfaRequired', e.target.checked)}
              />
              <span className="nx-toggle-slider" />
            </label>
          </div>

          {/* SSO */}
          <div className="nx-iam-policy-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="nx-iam-row-name">Enable SSO</span>
                <NotEnforcedBadge />
              </div>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                Allow single sign-on via a configured identity provider.
                {!hasProviders && <span style={{ color: '#b45309', marginLeft: 4 }}>Add an identity provider below before enabling.</span>}
              </div>
              <div style={{ marginTop: 6 }}>
                <Btn size="sm" variant="ghost" onClick={() => setShowSsoDrawer(true)} style={{ fontSize: 11 }}>
                  <NexusIcon name="KeySquare" size={12} style={{ marginRight: 4 }} />
                  {providerCountLabel}
                </Btn>
              </div>
            </div>
            <label className="nx-toggle">
              <input type="checkbox" aria-label="Enable SSO"
                checked={!!draft?.ssoEnabled}
                disabled={!canEdit || saving || !hasProviders}
                title={!hasProviders ? 'Configure an identity provider first' : undefined}
                onChange={e => setField('ssoEnabled', e.target.checked)}
              />
              <span className="nx-toggle-slider" />
            </label>
          </div>

          {/* Password Rotation */}
          <div className="nx-iam-policy-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="nx-iam-row-name">Password Rotation (days)</span>
                <NotEnforcedBadge />
              </div>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                Maximum password age in days. Members are prompted to rotate on expiry.
              </div>
            </div>
            <input type="number" className="nx-input" style={{ width: 100, textAlign: 'right' }}
              aria-label="Password Rotation (days)" value={draft?.passwordRotationDays ?? ''}
              disabled={!canEdit || saving} min={7} max={365}
              onChange={e => setField('passwordRotationDays', e.target.value === '' ? null : Number(e.target.value))}
            />
          </div>

          {/* Session Timeout */}
          <div className="nx-iam-policy-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="nx-iam-row-name">Session Timeout (min)</span>
                <NotEnforcedBadge />
              </div>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                Idle session timeout in minutes. Sessions expire after this period of inactivity.
              </div>
            </div>
            <input type="number" className="nx-input" style={{ width: 100, textAlign: 'right' }}
              aria-label="Session Timeout (min)" value={draft?.sessionTimeoutMinutes ?? ''}
              disabled={!canEdit || saving} min={5} max={43200}
              onChange={e => setField('sessionTimeoutMinutes', e.target.value === '' ? null : Number(e.target.value))}
            />
          </div>
        </div>
      </div>

      {/* Password Complexity */}
      <div className="nx-card">
        <div className="nx-card-head">
          <NexusIcon name="KeyRound" size={14} />
          <span>Password Complexity</span>
          <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--nx-text-muted)', marginLeft: 'auto' }}>
            Requirements for new and changed passwords
          </span>
        </div>
        <div style={{ padding: '4px 0' }}>
          <div className="nx-iam-policy-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="nx-iam-row-name">Minimum Length</span>
                <NotEnforcedBadge />
              </div>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                Minimum number of characters required. Recommended: 12 or more.
              </div>
            </div>
            <input type="number" className="nx-input" style={{ width: 100, textAlign: 'right' }}
              aria-label="Minimum password length" value={draft?.passwordMinLength ?? ''}
              disabled={!canEdit || saving} min={8} max={128}
              onChange={e => setField('passwordMinLength', e.target.value === '' ? null : Number(e.target.value))}
            />
          </div>
          {PW_COMPLEXITY_RULES.map(({ key, label, desc }) => (
            <div key={key} className="nx-iam-policy-row">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span className="nx-iam-row-name">{label}</span>
                  <NotEnforcedBadge />
                </div>
                <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>{desc}</div>
              </div>
              <label className="nx-toggle">
                <input type="checkbox" aria-label={label}
                  checked={!!draft?.[key]} disabled={!canEdit || saving}
                  onChange={e => setField(key, e.target.checked)}
                />
                <span className="nx-toggle-slider" />
              </label>
            </div>
          ))}
          <div className="nx-iam-policy-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span className="nx-iam-row-name">Password History (count)</span>
                <NotEnforcedBadge />
              </div>
              <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>
                Number of previous passwords that cannot be reused.
              </div>
            </div>
            <input type="number" className="nx-input" style={{ width: 100, textAlign: 'right' }}
              aria-label="Password history count" value={draft?.passwordHistoryCount ?? ''}
              disabled={!canEdit || saving} min={1} max={24}
              onChange={e => setField('passwordHistoryCount', e.target.value === '' ? null : Number(e.target.value))}
            />
          </div>
        </div>
      </div>

      {showSsoDrawer && (
        <SsoProviderDrawer
          orgId={orgId}
          providers={ssoProviders}
          onClose={() => setShowSsoDrawer(false)}
          onProvidersChange={setSsoProviders}
        />
      )}
    </>
  );
}

function SecurityTab({ orgId }) {
  const toast    = useToast();
  const navigate = useNavigate();
  const perm     = usePermissions();
  // F-02: all hooks above; gate is safe below
  const canView = perm.has(ORG.SECURITY_VIEW) || perm.isSA;
  const canEdit = perm.has(ORG.SECURITY_CONFIGURE) || perm.isSA;

  const [policies, setPolicies] = useState(null);
  const [draft,    setDraft]    = useState(null);
  const [busy,     setBusy]     = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [err,      setErr]      = useState(null);
  const [dirty,    setDirty]    = useState(false);

  const loadPolicies = useCallback(() => {
    setBusy(true); setErr(null);
    orgPoliciesApi.get(orgId)
      .then(r => { setPolicies(r); setDraft(r); setDirty(false); setBusy(false); })
      .catch(e => { setErr(e?.message || 'Failed to load security policies'); setBusy(false); });
  }, [orgId]);

  useEffect(() => { loadPolicies(); }, [loadPolicies]);

  if (!canView) return <DeniedState capability={ORG.SECURITY_VIEW} />;

  const setField = (key, val) => { setDraft(d => ({ ...d, [key]: val })); setDirty(true); };

  const save = async () => {
    setSaving(true);
    try {
      // eslint-disable-next-line no-unused-vars
      const { _enforced, ...payload } = draft;
      const updated = await orgPoliciesApi.update(orgId, payload);
      setPolicies(updated); setDraft(updated); setDirty(false);
      toast({ title: 'Security policies saved', tone: 'success' });
    } catch (e) {
      toast({ title: e?.message || 'Failed to save policies', tone: 'error' });
    } finally { setSaving(false); }
  };

  const reset = () => { setDraft(policies); setDirty(false); };

  // Toolbar always rendered so Refresh/Retry is reachable in every state
  const toolbar = (
    <div className="nx-iam-toolbar">
      <div className="nx-iam-toolbar-left">
        <h3 className="nx-iam-section-title">Authentication</h3>
      </div>
      <div className="nx-iam-toolbar-right">
        <Btn size="sm" variant="ghost" disabled={busy || dirty} onClick={loadPolicies}><NexusIcon name="RefreshCw" size={13} /></Btn>
        {canEdit && policies && (
          <>
            <Btn size="sm" variant="ghost" disabled={!dirty || saving} onClick={reset}>Reset</Btn>
            <Btn size="sm" variant="primary" disabled={!dirty || saving} onClick={save}>Save Changes</Btn>
          </>
        )}
      </div>
    </div>
  );

  if (busy) return (
    <div className="nx-iam-section">
      {toolbar}
      <div className="nx-loading"><div className="nx-spinner" /></div>
    </div>
  );

  if (err) return (
    <div className="nx-iam-section">
      {toolbar}
      <div role="alert" className="nx-iam-banner nx-iam-banner--error">
        <NexusIcon name="AlertCircle" size={14} className="nx-iam-banner-icon" />
        <div style={{ flex: 1 }}><strong>Failed to load security policies.</strong> {err}</div>
        <Btn size="sm" variant="ghost" onClick={loadPolicies}>Retry</Btn>
      </div>
    </div>
  );

  return (
    <div className="nx-iam-section">
      {toolbar}

      {/* ── Security Posture ── Category A features enforced at runtime ── */}
      <div className="nx-card">
        <div className="nx-card-head">
          <NexusIcon name="ShieldCheck" size={14} />
          <span>Security Posture</span>
          <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--nx-text-muted)', marginLeft: 'auto' }}>
            Platform-enforced controls — not configurable
          </span>
        </div>
        <div style={{ padding: '4px 0' }}>
          {POSTURE_A.map(item => (
            <div key={item.id} className="nx-iam-policy-row">
              <NexusIcon name={item.icon} size={14} style={{ color: item.partial ? '#d97706' : 'var(--nx-accent)', flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span className="nx-iam-row-name">{item.label}</span>
                  <EnforcedBadge partial={item.partial} />
                </div>
                <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>{item.desc}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Category B policy storage notice ── */}
      <div className="nx-iam-banner nx-iam-banner--info">
        <NexusIcon name="Info" size={14} style={{ flexShrink: 0, marginTop: 1 }} />
        <span>
          <strong>Authentication policy storage is active.</strong>{' '}
          Configurations are saved and will be enforced when runtime policy enforcement is enabled.
          Platform-enforced security controls are shown above.
        </span>
      </div>

      {/* ── Identity Security + Password Complexity (Category B) ── */}
      <IdentitySecuritySection draft={draft} canEdit={canEdit} saving={saving} setField={setField} orgId={orgId} />

      {/* ── Access Controls + Workspace Policies (Category B) ── */}
      {POLICY_SECTIONS.map(section => (
        <div key={section.id} className="nx-card">
          <div className="nx-card-head">
            <NexusIcon name={section.icon} size={14} />
            <span>{section.title}</span>
          </div>
          <div style={{ padding: '4px 0' }}>
            {section.policies.map(({ key, label, desc, type, min, max }) => (
              <div key={key} className="nx-iam-policy-row">
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <span className="nx-iam-row-name">{label}</span>
                    <NotEnforcedBadge />
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--nx-text-muted)', marginTop: 2 }}>{desc}</div>
                </div>
                {type === 'bool' ? (
                  <label className="nx-toggle">
                    <input
                      type="checkbox"
                      aria-label={label}
                      checked={!!draft?.[key]}
                      disabled={!canEdit || saving}
                      onChange={e => setField(key, e.target.checked)}
                    />
                    <span className="nx-toggle-slider" />
                  </label>
                ) : (
                  <input
                    type="number"
                    className="nx-input"
                    style={{ width: 100, textAlign: 'right' }}
                    aria-label={label}
                    value={draft?.[key] ?? ''}
                    disabled={!canEdit || saving}
                    min={min ?? 0}
                    max={max}
                    onChange={e => setField(key, e.target.value === '' ? null : Number(e.target.value))}
                  />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* ── Security Operations ── single link; Force Logout and Audit live in Security Center ── */}
      <div className="nx-card">
        <div className="nx-card-head">
          <NexusIcon name="Terminal" size={14} />
          <span>Security Operations</span>
          <span style={{ fontSize: 11, fontWeight: 400, color: 'var(--nx-text-muted)', marginLeft: 'auto' }}>
            Active sessions, IP allowlist, and security events
          </span>
        </div>
        <div>
          <button className="nx-iam-quick-action" onClick={() => navigate(`/organization/${orgId}/security`)}>
            <NexusIcon name="ShieldCheck" size={14} style={{ color: 'var(--nx-accent)', flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--nx-text)' }}>Open Security Center</div>
              <div style={{ fontSize: 11, color: 'var(--nx-text-muted)' }}>
                Manage active sessions, revoke access, configure IP allowlist, and review security events.
              </div>
            </div>
            <NexusIcon name="ChevronRight" size={13} style={{ color: 'var(--nx-text-faint)', flexShrink: 0 }} />
          </button>
        </div>
      </div>
    </div>
  );
}





/* ── Main component ───────────────────────────────────────────────────── */
export default function OrgIAM() {
  const { orgId } = useParams();
  const perm  = usePermissions();
  const org   = useOrganization();
  const toast = useToast();

  const [searchParams, setSearchParams] = useSearchParams();
  const rawTab    = searchParams.get('tab') || 'assignments';
  const activeTab = LEGACY_TAB_MAP[rawTab] ?? rawTab;
  const setActiveTab = (id) => setSearchParams({ tab: id }, { replace: true });
  const preselectedWsId     = searchParams.get('wsId') ?? undefined;
  const preselectedMemberId = searchParams.get('memberId') ?? undefined;
  const returnTo    = searchParams.get('returnTo');
  const returnLabel = searchParams.get('returnLabel') || 'Back';

  // Redirect legacy tab IDs to their new equivalents
  useEffect(() => {
    if (LEGACY_TAB_MAP[rawTab]) {
      setSearchParams(p => { const n = new URLSearchParams(p); n.set('tab', LEGACY_TAB_MAP[rawTab]); return n; }, { replace: true });
    }
  }, [rawTab, setSearchParams]);

  // F-08: bust the 5-min runtime cache on every IAM load so permission state is always fresh
  useEffect(() => { invalidateRuntime(orgId); }, [orgId]);

  // F-06: verify the current user actually belongs to this org (prevents cross-org URL tricks)
  const userContexts = window.__nx_auth?.user?.contexts ?? [];
  const orgAccessible = perm.isSA || userContexts.some(c => c.orgId === orgId);

  // C-03: Load workspaces from API — do not depend on injected route data prop
  // M-05: normalize workspace objects so field names are consistent (orgId vs org.id)
  const [wss, setWss] = useState([]);
  useEffect(() => {
    let alive = true;
    listWorkspaces(orgId, { pageSize: 100 })
      .then(r => {
        if (!alive) return;
        const raw = r?.workspaces ?? r?.data ?? r ?? [];
        setWss(raw.map(w => ({
          ...w,
          orgId: w.orgId ?? w.org?.id ?? orgId,
          name:  w.name  ?? w.slug ?? w.id,
          slug:  w.slug  ?? w.name ?? '',
        })));
      })
      .catch(e => { if (!alive) return; toast({ title: e?.message || 'Failed to load workspaces', tone: 'error' }); });
    return () => { alive = false; };
  }, [orgId, toast]);

  if (!orgAccessible) {
    return (
      <div className="nx-cc-page">
        <PageHead title="Identity & Access" sub={org?.name} back={returnTo ? { label: `Back to ${returnLabel}`, onClick: () => navigate(returnTo) } : undefined} />
        <DeniedState reason="org_not_found" message="You are not a member of this organization." />
      </div>
    );
  }

  if (!perm.has(ORG.MEMBERS_VIEW) && !perm.isSA) {
    return (
      <div className="nx-cc-page">
        <PageHead title="Identity & Access" sub={org?.name} back={returnTo ? { label: `Back to ${returnLabel}`, onClick: () => navigate(returnTo) } : undefined} />
        <DeniedState
          reason="insufficient_permission"
          message="Identity & Access management requires the org.members.view capability. Contact your org admin to request access."
        />
      </div>
    );
  }

  return (
    <div className="nx-cc-page">
      <PageHead title="Identity & Access" sub={org?.name ?? 'Organization'} back={returnTo ? { label: `Back to ${returnLabel}`, onClick: () => navigate(returnTo) } : undefined} />

      <TabBar tabs={TABS} active={activeTab} onChange={setActiveTab} />

      {activeTab === 'assignments' && <AssignmentsTab orgId={orgId} wss={wss} preselectedMemberId={preselectedMemberId} preselectedWsId={preselectedWsId} />}
      {activeTab === 'roles'       && <RolesTab orgId={orgId} />}
      {activeTab === 'authentication' && <SecurityTab orgId={orgId} />}

    </div>
  );
}
